import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import type { GhmSignalKind } from "@ghm/mqtt-contract";

import { useModalA11y } from "@/lib/useModalA11y";
import { parseTag } from "@/features/machines/addressing";
import { resolveTone } from "../selectors";
import type {
  ChartSeries,
  DashboardWidget,
  ResolvedWidgetValue,
  WidgetLayout,
  WidgetSettings,
  WidgetSize,
  WidgetThreshold,
  WidgetType,
} from "../types";
import { requiresMultipleSources, widgetSources } from "../types";
import {
  isProductionModeCompatible,
  productionModesForSignal,
  resolveProductionTotalMode,
  type ProductionMode,
  type ProductionTotalMode,
} from "../productionSources";
import { bucketMsForWindow, historicalProductionWindow, windowDurationMs } from "../chartWidgets";
import { combinableWith } from "../multiSource";
import {
  compatibleTypesFor,
  discouragedNoteFor,
  isTypeCompatible,
  recommendedTypeFor,
} from "../widgetCompatibility";
import type { WidgetAvailabilityState } from "../useAvailability";
import type { WidgetHistoryState } from "../useProductionHistories";
import { SignalCard } from "./SignalCard";
import { StateMappingFields, parseStateMappingDraft, type StateMappingDraft } from "./StateMappingFields";

const CHART_SERIES_LABEL: Record<ChartSeries, string> = {
  rate: "Ritmo (velocidade)",
  throughput: "Produção por período",
  cumulative: "Acumulado",
};

function chartSeriesLabel(
  series: ChartSeries,
  signalKind: GhmSignalKind | undefined,
): string {
  if (series === "rate" && signalKind === "instantaneous") {
    return "Leitura ao longo do tempo";
  }
  if (series === "rate" && signalKind === "state") return "Código ao longo do tempo";
  if (series === "rate" && signalKind === "cumulative") {
    return "Valor acumulado ao longo do tempo";
  }
  return CHART_SERIES_LABEL[series];
}

/**
 * Série sintética para o preview de gráficos no editor — o painel real busca
 * `/production/history`, mas o modal não. Sem isto o preview de `line-chart`/
 * `bar-chart` ficaria preso em "Carregando histórico…". Os valores são
 * ilustrativos (não vêm do device).
 *
 * `seed` desloca a fase da onda por FONTE. Sem ele todas as séries de um
 * comparativo saíam idênticas: o preview desenhava N curvas exatamente
 * sobrepostas (só a última cor visível) e uma legenda com N itens — mostrando
 * o oposto do que o card existe para mostrar, que é o desequilíbrio entre as
 * fontes.
 */
function makeSamplePreviewHistory(
  widget: DashboardWidget,
  seed = 0,
): WidgetHistoryState | undefined {
  if (
    widget.widgetType !== "line-chart" &&
    widget.widgetType !== "bar-chart" &&
    widget.widgetType !== "multi-chart" &&
    !historicalProductionWindow(widget)
  ) {
    return undefined;
  }
  const window = historicalProductionWindow(widget) ?? widget.settings.chart?.window ?? widget.settings.history?.window ?? "24h";
  const stepMs = bucketMsForWindow(window);
  const now = Date.now();
  const count = 24;
  let acc = 1000;
  const points = Array.from({ length: count }, (_, index) => {
    const rate = Math.round(
      (Math.sin(index / 2 + seed * 0.8) * 0.5 + 0.5) * 40 * (1 - seed * 0.12),
    );
    acc += rate;
    return {
      bucket: new Date(now - (count - 1 - index) * stepMs).toISOString(),
      configHashHex: "0xPREVIEW",
      accFirst: acc - rate,
      accLast: acc,
      delta: rate,
      vLast: rate,
      vAvg: rate,
      vMin: 0,
      vMax: rate,
      sampleCount: 60,
      activeSeconds: 60,
      quality: 1,
    };
  });
  return {
    status: "ready",
    data: {
      deviceId: "preview",
      portIndex: 0,
      window,
      resolution: stepMs === 60_000 ? "1m" : stepMs === 86_400_000 ? "1d" : "1h",
      from: points[0].bucket,
      to: points[points.length - 1].bucket,
      total: points.reduce((sum, point) => sum + point.delta, 0),
      points,
    },
  };
}

/** Disponibilidade sintética para o preview do widget `utilization`. */
function makeSamplePreviewAvailability(
  widget: DashboardWidget,
): WidgetAvailabilityState | undefined {
  if (widget.widgetType !== "utilization") return undefined;
  const window = widget.settings.history?.window ?? "24h";
  const windowSeconds = windowDurationMs(window) / 1000;
  const now = Date.now();
  const fromMs = now - windowSeconds * 1000;
  // Alterna rodando/parado em ~14 blocos (cada 3º bloco é parada) para a onda
  // quadrada do preview ter forma e horários.
  const blocks = 14;
  const blockSeconds = windowSeconds / blocks;
  const episodes = Array.from({ length: blocks }, (_, index) => {
    const state = index % 3 === 2 ? "stopped" : "running";
    const start = fromMs + index * blockSeconds * 1000;
    return {
      state: state as "running" | "stopped" | "gap",
      from: new Date(start).toISOString(),
      to: new Date(start + blockSeconds * 1000).toISOString(),
      seconds: blockSeconds,
    };
  });
  const runningSeconds = episodes
    .filter((episode) => episode.state === "running")
    .reduce((sum, episode) => sum + episode.seconds, 0);
  const observedSeconds = windowSeconds;
  return {
    status: "ready",
    data: {
      deviceId: "preview",
      portIndex: 0,
      window,
      resolution: bucketMsForWindow(window) === 60_000 ? "1m" : "1h",
      from: new Date(fromMs).toISOString(),
      to: new Date(now).toISOString(),
      windowSeconds,
      observedSeconds,
      runningSeconds,
      stoppedSeconds: observedSeconds - runningSeconds,
      gapSeconds: 0,
      bridgedSeconds: 0,
      utilizationPercent: (runningSeconds / observedSeconds) * 100,
      episodes,
    },
  };
}

/**
 * Layout de um card que ja existe.
 *
 * O editor reconstroi o widget inteiro a partir do formulario, e o formulario
 * nao tem nenhum campo de layout: tudo que esta aqui foi definido pelo ARRASTO
 * no painel. Campo que este helper esquecer e campo apagado em silencio ao
 * salvar qualquer outra coisa.
 *
 * O TAMANHO em celulas ja era preservado. A POSICAO nao era, e a consequencia
 * era visivel: o operador arrumava o painel, abria um card so para corrigir o
 * nome, e ao salvar o card voltava a fluir automaticamente — pulando de lugar
 * enquanto todos os vizinhos, esses sim com posicao gravada, ficavam parados.
 */
function preservarLayoutDoArrasto(
  existingWidget: DashboardWidget | undefined,
  size: WidgetSize,
  order: number,
): WidgetLayout {
  const arrastado = existingWidget?.layout;
  return {
    size,
    order,
    ...(arrastado?.w !== undefined ? { w: arrastado.w } : null),
    ...(arrastado?.h !== undefined ? { h: arrastado.h } : null),
    ...(arrastado?.x !== undefined ? { x: arrastado.x } : null),
    ...(arrastado?.y !== undefined ? { y: arrastado.y } : null),
  };
}

/**
 * A marca de "de onde este grafico veio", preservada atraves do editor.
 *
 * "Gerar grafico" SUBSTITUI o card de origem, e o desfazer depende inteiramente
 * de `settings.chartOrigin`. Como o editor reconstroi `settings` campo a campo,
 * a marca era descartada ao salvar: bastava corrigir o titulo do grafico para o
 * "Ocultar grafico" desaparecer e a substituicao virar definitiva.
 *
 * A marca so nao sobrevive quando deixa de significar algo — quando o operador
 * leva o card de volta, na mao, ao tipo de origem: ai nao ha mais o que
 * desfazer.
 */
function preservarOrigemDoGrafico(
  existingWidget: DashboardWidget | undefined,
  widgetType: WidgetType,
): WidgetSettings["chartOrigin"] {
  const origem = existingWidget?.settings.chartOrigin;
  if (!origem || origem.widgetType === widgetType) return undefined;
  return origem;
}

export interface AvailableSource {
  /** Tag técnica usada em fórmulas (ex.: `G0_P0_s`, `V0`). */
  source: string;
  /** Rótulo amigável vindo do `metaJson` ou do label do campo calculado. */
  label: string;
  /** Rótulo curto para legenda dentro de comparativos (ex.: "Tensão L1"). */
  shortLabel?: string;
  /** "signal" = vem do gateway; "calc" = campo calculado local. */
  kind: "signal" | "calc";
  /** Tipo do sinal: `s` (boolean), `acc` (totalizador), `v` (instantâneo). Vazio para `V{n}`. */
  signal?: "s" | "acc" | "v";
  /**
   * SEMÂNTICA declarada do valor (`resolveSignalKind`). Ausente quando não dá
   * para saber — em modo edição a fonte é deduzida da tag salva, sem `/meta`
   * por perto para dizer o modo. Nesse caso a matriz cai no conjunto legado
   * por sufixo, que é permissivo: errar para o lado de não bloquear um widget
   * que já existe.
   */
  signalKind?: GhmSignalKind;
  /** Unidade publicada no `metaJson.functions[].values[signal].unit`, se houver. */
  suggestedUnit?: string;
  /**
   * Faixa de engenharia e casas DECLARADAS no `/meta`. O caminho de widget
   * automatico ja as usava; o editor nao — e como o tanque NUNCA nasce
   * automaticamente, a faixa declarada nunca chegava nele. Um nivel de 8,9 m
   * num tanque de 0-10 m aparecia como ~9% cheio numa escala 0-100.
   */
  suggestedMin?: number;
  suggestedMax?: number;
  suggestedDecimals?: number;
  /** Modo da função (DI/CT/PM/HSC/LATCH/RST), só informativo. */
  mode?: string;
}

interface WidgetEditorModalProps {
  /**
   * Quando informado, modal abre em modo "editar" — campos pré-preenchidos
   * e select de fonte travado. Quando ausente, modal abre em modo "criar".
   */
  existingWidget?: DashboardWidget;
  /** Lista de fontes disponíveis. Obrigatória no modo criar. */
  sources?: AvailableSource[];
  /** Seções já usadas no painel — sugeridas em vez de exigir digitação exata. */
  knownSections?: string[];
  machineId: string;
  /** Próxima ordem disponível (modo criar). */
  nextOrder?: number;
  /**
   * Valor resolvido em tempo real para a fonte selecionada — alimenta o
   * preview ao vivo. Se ausente, o preview usa um snapshot exemplo.
   */
  resolveLiveValue?: (source: string) => ResolvedWidgetValue | null;
  onClose: () => void;
  onSave: (widget: DashboardWidget) => void;
  /** Em modo editar, remove o widget. Opcional. */
  onRemove?: () => void;
  /** Em modo editar com G-source que tem SignalConfig persistido — apaga essa config local. */
  onResetSignalConfig?: () => void;
}

/**
 * Cada tipo recebe um glyph na label para identificação rápida no dropdown.
 * Mantemos os glyphs como prefixo direto da `label` para não depender de
 * custom dropdown — `<option>` aceita texto Unicode normalmente.
 *
 * `category` controla o agrupamento no dropdown via `<optgroup>`:
 * - "reading": leitura direta do sinal (LED, Number, Gauge, Progress)
 * - "analysis": análise computada (Availability, Production-status, KPI)
 *
 * Operador casual abre o dropdown e enxerga primeiro o grupo "Leitura direta"
 * porque é o que cobre 80% dos casos (visualizar um valor publicado pelo
 * gateway). O grupo "Análise" oferece os widgets de OEE-light.
 */
type TypeOption = {
  value: WidgetType;
  label: string;
  hint: string;
  category: "reading" | "analysis";
};

const TYPE_OPTIONS: TypeOption[] = [
  { value: "state-value", label: "Estado por código", hint: "Texto e cor para cada código declarado.", category: "reading" },
  // Leitura direta
  { value: "led", label: "💡 LED / Estado", hint: "Para sinais booleanos (s = 0/1).", category: "reading" },
  { value: "number", label: "🔢 Número grande", hint: "Valor numérico simples.", category: "reading" },
  { value: "gauge", label: "📊 Gauge / Ponteiro", hint: "Para velocidade / vazão / taxa.", category: "reading" },
  // Análise da máquina
  {
    value: "availability",
    label: "✅ Disponibilidade",
    hint: "Mede disponibilidade por estado ou por atividade do sinal.",
    category: "analysis",
  },
  {
    value: "utilization",
    label: "⏱ Disponibilidade do período",
    hint: "Utilização, tempo rodando × parado e timeline por janela.",
    category: "analysis",
  },
  {
    value: "production-status",
    label: "⚙ Status operacional",
    hint: "Mostra produção por estado ou movimento por contagem, velocidade ou vazão.",
    category: "analysis",
  },
  {
    value: "production-kpi",
    label: "📦 Produção / Lote",
    hint: "Mostra ritmo, total físico, acumulado zerável ou lote com meta.",
    category: "analysis",
  },
  {
    value: "line-chart",
    label: "📉 Tendência histórica",
    hint: "Mostra a série oficial persistida no Timescale.",
    category: "analysis",
  },
  {
    value: "bar-chart",
    label: "📊 Produção por período",
    hint: "Barras da produção por intervalo (delta por bucket).",
    category: "analysis",
  },
  {
    value: "multi-value",
    label: "🔀 Painel comparativo",
    hint: "Várias grandezas da mesma unidade lado a lado (tensão L1/L2/L3).",
    category: "reading",
  },
  {
    value: "multi-chart",
    label: "🔀 Gráfico multissérie",
    hint: "Várias séries no mesmo eixo, para comparar fases ou pontos.",
    category: "analysis",
  },
  {
    value: "modbus-health",
    label: "🩺 Saúde Modbus",
    hint: "Quantos pontos do barramento estão ok, mudos ou em exceção.",
    category: "analysis",
  },
  {
    value: "tank",
    label: "🛢 Tanque / nível",
    hint: "Preenchimento entre mínimo e máximo, com alarmes de baixo e alto.",
    category: "reading",
  },
];

const PRODUCTION_MODE_OPTIONS: Array<{ value: ProductionMode; label: string }> = [
  { value: "rate", label: "Ritmo atual" },
  { value: "hourly", label: "Projeção por hora" },
  { value: "total", label: "Total acumulado" },
];

const CATEGORY_LABELS: Record<TypeOption["category"], string> = {
  reading: "Leitura direta",
  analysis: "Análise da máquina",
};

/**
 * "Familias" de widget (Incremento B): consolidam pares de tipos que confundiam
 * o operador num dropdown menor + um seletor de sub-modo. E SO APRESENTACAO — os
 * quatro tipos reais (`availability`, `utilization`, `production-status`,
 * `production-kpi`) continuam sendo a fonte da verdade em `widgetType`, nos
 * renderers e no storage. A familia so muda como o operador ESCOLHE:
 *  - "Disponibilidade" -> availability (indicador) ou utilization (timeline);
 *  - "Producao"        -> production-kpi (ritmo/total/lote) ou production-status
 *                         (estado/movimento).
 * `members` esta em ordem de preferencia de default.
 */
type WidgetFamily =
  | "state-value"
  | "led"
  | "number"
  | "gauge"
  | "disponibilidade"
  | "producao"
  | "line-chart"
  | "bar-chart"
  | "comparativo"
  | "tank"
  | "modbus-health";

interface FamilyOption {
  family: WidgetFamily;
  label: string;
  hint: string;
  category: "reading" | "analysis";
  members: WidgetType[];
  subLabel?: string;
  subHint?: string;
  memberLabels?: Partial<Record<WidgetType, string>>;
}

const FAMILY_OPTIONS: FamilyOption[] = [
  { family: "state-value", label: "Estado por código", hint: "Texto e cor definidos pelo operador, sem adivinhar o significado.", category: "reading", members: ["state-value"] },
  { family: "led", label: "💡 LED / Estado", hint: "Para sinais booleanos (s = 0/1).", category: "reading", members: ["led"] },
  { family: "number", label: "🔢 Número grande", hint: "Valor numérico simples.", category: "reading", members: ["number"] },
  { family: "gauge", label: "📊 Gauge / Ponteiro", hint: "Para velocidade / vazão / taxa.", category: "reading", members: ["gauge"] },
  {
    family: "disponibilidade",
    label: "✅ Disponibilidade",
    hint: "Disponibilidade da máquina — como indicador ou linha do tempo.",
    category: "analysis",
    members: ["availability", "utilization"],
    subLabel: "Como mostrar",
    subHint: "Indicador resume num número; a linha do tempo mostra rodando × parado na janela.",
    memberLabels: {
      availability: "Indicador (%)",
      utilization: "Linha do tempo do período",
    },
  },
  {
    family: "producao",
    label: "📦 Produção",
    hint: "Produção da máquina — por ritmo/total/lote ou por estado/movimento.",
    category: "analysis",
    members: ["production-kpi", "production-status"],
    subLabel: "O que medir",
    subHint: "Ritmo/total/lote conta quantidade; estado/movimento mostra a condição operacional.",
    memberLabels: {
      "production-kpi": "Ritmo, total ou lote",
      "production-status": "Estado / movimento",
    },
  },
  { family: "line-chart", label: "📉 Tendência histórica", hint: "Mostra a série oficial persistida no Timescale.", category: "analysis", members: ["line-chart"] },
  { family: "bar-chart", label: "📊 Produção por período", hint: "Barras da produção por intervalo (delta por bucket).", category: "analysis", members: ["bar-chart"] },
  {
    family: "modbus-health",
    label: "🩺 Saúde Modbus",
    hint: "Diagnóstico do barramento inteiro, em vez de um card por registrador.",
    category: "analysis",
    members: ["modbus-health"],
  },
  {
    family: "tank",
    label: "🛢 Tanque / nível",
    hint: "Nível entre mínimo e máximo — o operador escolhe, não nasce sozinho.",
    category: "reading",
    members: ["tank"],
  },
  {
    family: "comparativo",
    label: "🔀 Comparativo",
    hint: "Compara grandezas da mesma unidade — agora ou ao longo do tempo.",
    category: "analysis",
    members: ["multi-value", "multi-chart"],
    subLabel: "Como comparar",
    subHint: "O painel mostra os valores atuais lado a lado; o gráfico compara as séries no mesmo eixo.",
    memberLabels: {
      "multi-value": "Valores agora, lado a lado",
      "multi-chart": "Séries no mesmo eixo",
    },
  },
];

const FAMILY_OF: Record<WidgetType, WidgetFamily> = {
  "state-value": "state-value",
  led: "led",
  number: "number",
  gauge: "gauge",
  progress: "number",
  availability: "disponibilidade",
  utilization: "disponibilidade",
  "production-status": "producao",
  "production-kpi": "producao",
  "line-chart": "line-chart",
  "bar-chart": "bar-chart",
  "multi-value": "comparativo",
  "multi-chart": "comparativo",
  tank: "tank",
  "modbus-health": "modbus-health",
};

function familyOptionOf(family: WidgetFamily): FamilyOption {
  return FAMILY_OPTIONS.find((option) => option.family === family) ?? FAMILY_OPTIONS[0];
}

/** Membros da familia compativeis com a fonte atual, na ordem de preferencia. */
function compatibleMembers(option: FamilyOption, allowed: Set<WidgetType>): WidgetType[] {
  return option.members.filter((member) => allowed.has(member));
}

/**
 * Tipo concreto ao escolher/entrar numa familia: preserva o tipo atual se ele ja
 * pertence a essa familia e e compativel; senao, o primeiro membro compativel.
 */
function resolveFamilyMember(
  option: FamilyOption,
  allowed: Set<WidgetType>,
  current: WidgetType,
): WidgetType {
  if (FAMILY_OF[current] === option.family && allowed.has(current)) return current;
  return compatibleMembers(option, allowed)[0] ?? option.members[0];
}


function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <small className="field__error" role="alert" style={{ color: "var(--danger-600)", fontSize: 11 }}>
      {message}
    </small>
  );
}

/**
 * Modal único de criar/editar widget operacional.
 *
 * Cobre toda a configuração do `DashboardWidget`: fonte, tipo, nome,
 * unidade, escala, decimais, limites de alerta, tamanho do card, alarme
 * visual, polaridade do LED (sinais `_s`) e ocultação. À direita, mostra
 * um preview ao vivo do `SignalCard` reagindo a cada digitação.
 *
 * Substitui `AddWidgetModal` e `SignalConfigModal` que existiam separados.
 */
export function WidgetEditorModal({
  existingWidget,
  sources,
  knownSections = [],
  machineId,
  nextOrder = 0,
  resolveLiveValue,
  onClose,
  onSave,
  onRemove,
  onResetSignalConfig,
}: WidgetEditorModalProps) {
  const isEditMode = !!existingWidget;
  const sortedSources = useMemo(
    () =>
      [...(sources ?? [])].sort((a, b) => {
        if (a.kind !== b.kind) return a.kind === "signal" ? -1 : 1;
        // `numeric` nao e detalhe de gosto: a comparacao de texto pura ordena
        // "G0_P10_v" ANTES de "G0_P2_v", porque compara "1" com "2" no
        // primeiro digito. Num multimedidor Modbus, que declara ate 16 pontos
        // numa porta so, a lista saía P0, P1, P10, P11 ... P2 — e achar a
        // "Tensao L2" virava caca ao tesouro.
        return a.source.localeCompare(b.source, "pt-BR", { numeric: true });
      }),
    [sources],
  );
  const signalSources = useMemo(
    () => sortedSources.filter((source) => source.kind === "signal"),
    [sortedSources],
  );

  // Estado inicial:
  // - Em editar: vem do existingWidget;
  // - Em criar: vem da primeira fonte da lista (sortedSources[0]).
  const initialSource = existingWidget?.source ?? signalSources[0]?.source ?? "";
  const [selectedSource, setSelectedSource] = useState(initialSource);
  const [sourceDefaultsReset, setSourceDefaultsReset] = useState(false);
  const [extraSources, setExtraSources] = useState<string[]>(
    existingWidget?.extraSources ?? [],
  );
  const [widgetType, setWidgetType] = useState<WidgetType>(
    existingWidget?.widgetType ?? recommendedTypeFor(sortedSources[0]),
  );
  const [title, setTitle] = useState(
    existingWidget?.title ?? sortedSources[0]?.label ?? "",
  );
  /**
   * O operador DIGITOU o titulo. E a unica prova confiavel de escolha: o campo
   * tambem e preenchido sozinho de tres lugares diferentes, cada um com sua
   * heuristica de "parece um default", e adivinhar pela string foi exatamente
   * o que deixou um default antigo virar o nome de um sinal. Aqui nao ha
   * adivinhacao — ou a tecla foi pressionada, ou nao foi.
   */
  const titleEditedRef = useRef(existingWidget?.settings.renamedByOperator === true);
  const [section, setSection] = useState(existingWidget?.section ?? "");
  const [unit, setUnit] = useState(
    existingWidget?.settings.unit ?? sortedSources[0]?.suggestedUnit ?? "",
  );
  const [min, setMin] = useState(
    String(existingWidget?.settings.min ?? sortedSources[0]?.suggestedMin ?? 0),
  );
  const [max, setMax] = useState(
    String(existingWidget?.settings.max ?? sortedSources[0]?.suggestedMax ?? 100),
  );
  const [decimals, setDecimals] = useState(String(existingWidget?.settings.decimals ?? 1));
  // O tamanho passou a ser da MALHA — o operador arrasta o canto do card no
  // painel. O editor so CARREGA o que ja existe, para nao apagar a escolha ao
  // salvar outra coisa; nao ha mais como escolher tamanho por aqui, senao dois
  // controles definiriam a mesma coisa e o ultimo a salvar venceria.
  // O enum antigo e REPASSADO, nao recalculado. Deriva-lo pelo tipo do widget
  // fazia abrir e salvar sem mudar nada trocar `md`
  // por `sm`: o editor gravava um tamanho que ninguem escolheu. O tamanho de
  // verdade vive em `layout.w`/`h`, preservado logo abaixo.
  const size = existingWidget?.layout.size ?? "md";
  const [alarmEnabled, setAlarmEnabled] = useState<boolean>(
    existingWidget?.settings.alarmEnabled ?? false,
  );
  const initialWarnThreshold = existingWidget?.settings.thresholds?.find((t) => t.tone === "warn");
  const initialDangerThreshold = existingWidget?.settings.thresholds?.find((t) => t.tone === "danger");
  const initialWarn = initialWarnThreshold?.at;
  const initialDanger = initialDangerThreshold?.at;
  const [warnAt, setWarnAt] = useState(initialWarn !== undefined ? String(initialWarn) : "");
  const [dangerAt, setDangerAt] = useState(
    initialDanger !== undefined ? String(initialDanger) : "",
  );
  const [warnDirection, setWarnDirection] = useState<"above" | "below">(
    initialWarnThreshold?.direction === "below" ? "below" : "above",
  );
  const [dangerDirection, setDangerDirection] = useState<"above" | "below">(
    initialDangerThreshold?.direction === "below" ? "below" : "above",
  );
  const [activeWhen, setActiveWhen] = useState<0 | 1>(
    existingWidget?.settings.activeWhen ?? 1,
  );
  const [stateRows, setStateRows] = useState<StateMappingDraft[]>(() =>
    existingWidget?.settings.stateMappings?.map((row) => ({ ...row, value: String(row.value) })) ??
    [{ value: "0", label: "", tone: "neutral" }, { value: "1", label: "", tone: "neutral" }],
  );
  const [onLabel, setOnLabel] = useState(existingWidget?.settings.stateLabels?.on ?? "Ligado");
  const [offLabel, setOffLabel] = useState(
    existingWidget?.settings.stateLabels?.off ?? "Desligado",
  );
  const [unavailableLabel, setUnavailableLabel] = useState(
    existingWidget?.settings.stateLabels?.unavailable ?? "Sem sinal",
  );
  const [availabilityMode, setAvailabilityMode] = useState<"state" | "activity">(
    existingWidget?.settings.availability?.mode ?? "state",
  );
  const [availabilityWindow, setAvailabilityWindow] = useState<
    NonNullable<DashboardWidget["settings"]["availability"]>["window"]
  >(existingWidget?.settings.availability?.window ?? "24h");
  const [minActiveValue, setMinActiveValue] = useState(
    String(existingWidget?.settings.availability?.minActiveValue ?? 0),
  );
  const [productionMode, setProductionMode] = useState<ProductionMode>(
    existingWidget?.settings.production?.mode ?? "rate",
  );
  const [productionMultiplier, setProductionMultiplier] = useState(
    String(existingWidget?.settings.production?.multiplier ?? 60),
  );
  const [productionTarget, setProductionTarget] = useState(
    existingWidget?.settings.production?.target !== undefined
      ? String(existingWidget.settings.production.target)
      : "",
  );
  const [productionTotalMode, setProductionTotalMode] = useState<ProductionTotalMode>(
    resolveProductionTotalMode(existingWidget?.settings.production),
  );
  const [productionWindow, setProductionWindow] = useState<
    NonNullable<WidgetSettings["production"]>["window"]
  >(existingWidget?.settings.production?.window ?? "current");
  const [historyWindow, setHistoryWindow] = useState<
    NonNullable<WidgetSettings["history"]>["window"]
  >(
    existingWidget?.settings.chart?.window ??
      existingWidget?.settings.history?.window ??
      "24h",
  );
  // Cortes de sinalização da disponibilidade (bom ≥ / atenção <), por processo.
  const [utilGood, setUtilGood] = useState<string>(
    String(existingWidget?.settings.utilization?.goodAtPercent ?? 60),
  );
  const [utilWarn, setUtilWarn] = useState<string>(
    String(existingWidget?.settings.utilization?.warnAtPercent ?? 30),
  );
  // Configuração do gráfico (line-chart/bar-chart). A forma (linha/barra) vem
  // do dropdown de tipo; aqui ficam a série e a escala do eixo Y. A janela
  // reusa `historyWindow`.
  const [chartSeries, setChartSeries] = useState<ChartSeries>(
    existingWidget?.settings.chart?.series ?? "throughput",
  );
  const [chartYScale, setChartYScale] = useState<"auto" | "fixed">(
    existingWidget?.settings.chart?.yScale ?? "auto",
  );
  // Alarme de PARADA (ritmo, estado de produção, LED): liga/tolerância.
  const [stopAlarmEnabled, setStopAlarmEnabled] = useState<boolean>(
    existingWidget?.settings.stopAlarm?.enabled ?? false,
  );
  const [stopAlarmMinutes, setStopAlarmMinutes] = useState<string>(
    String(existingWidget?.settings.stopAlarm?.afterMinutes ?? 0),
  );
  // Alarmes opt-in: por padrão um widget NÃO tem alarme. A seção só abre se o
  // widget já vem com algum alarme (edição) ou se o operador clica "Adicionar
  // alarme". Enquanto fechada, thresholds/alarme visual/parada não são
  // aplicados — o card nasce neutro, sem PERIGO no preview.
  const [alarmsOpen, setAlarmsOpen] = useState<boolean>(
    Boolean(
      initialWarn !== undefined ||
        initialDanger !== undefined ||
        existingWidget?.settings.alarmEnabled ||
        existingWidget?.settings.stopAlarm?.enabled,
    ),
  );
  const removeAllAlarms = () => {
    setWarnAt("");
    setDangerAt("");
    setAlarmEnabled(false);
    setStopAlarmEnabled(false);
    setAlarmsOpen(false);
  };
  // Cortes da disponibilidade resolvidos e saneados (0–100, `warn ≤ good`).
  const utilizationSetting = useMemo(() => {
    const clamp = (raw: number, fallback: number) =>
      Number.isFinite(raw) ? Math.max(0, Math.min(100, Math.round(raw))) : fallback;
    const good = clamp(Number(utilGood), 60);
    const warn = clamp(Number(utilWarn), 30);
    return { goodAtPercent: good, warnAtPercent: Math.min(warn, good) };
  }, [utilGood, utilWarn]);
  // Erros de validação por campo (H1.4). `_global` é reservado para erros
  // que não pertencem a um campo específico (ex.: "selecione uma fonte").
  // Cada submit limpa antes de revalidar; a UI mostra a primeira mensagem
  // associada ao campo em `<small className="field__error">`.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // H1.2 — confirmação rica em "Voltar ao padrão do sinal".
  // Em vez de chamar `onResetSignalConfig` direto no click, abre um painel
  // inline listando os campos personalizados que serão perdidos. Operador
  // tem dois cliques explícitos para confirmar.
  const [confirmingReset, setConfirmingReset] = useState(false);

  // H3 — simulador de preview.
  // "live" (default) usa `resolveLiveValue(source)`; "simulate" gera um valor
  // sintético a partir dos controles abaixo do card. Sem dependência de
  // telemetria, permite ao operador visualizar tons/thresholds/labels antes
  // de salvar.
  const [previewMode, setPreviewMode] = useState<"live" | "simulate">("live");
  // Slider numérico — usado por Number/Gauge/Progress/Production-KPI.
  // Inicia em 50 mas é reajustado pelo useEffect a cada troca de tipo.
  const [simNumeric, setSimNumeric] = useState<number>(50);
  // Switch booleano — usado por LED e pelas análises (Availability/ProdStatus).
  // Em LED: 1 = ligado, 0 = desligado. Em análises: 1 = disponível/produzindo,
  // 0 = indisponível/parado.
  const [simBoolState, setSimBoolState] = useState<0 | 1>(1);
  // Toggle "Sem sinal" — força isAvailable=false em qualquer tipo.
  const [simIsAvailable, setSimIsAvailable] = useState<boolean>(true);
  // Production-KPI específico: status do acumulado simulado.

  // H5.1 — agrupamento visual "Mais opções de exibição".
  // Os campos avançados (escala, decimais, limites de alerta, alarme) ficam
  // num fieldset secundário com legend muted — abaixo dos campos primários
  // (tipo, nome, regra). Não é collapsable: optamos por sempre visível para
  // garantir descoberta e compatibilidade com fluxo de teste.

  // H1.3 — detecção de alterações não salvas:
  // Captura no mount um snapshot serializado do estado inicial do form.
  // A cada render, `isDirty` é true se o JSON atual diverge — comparação
  // estrutural simples, baixo custo para um form pequeno. O snapshot
  // serializado mora em `initialSnapshotRef` (definido pelo primeiro
  // `previewWidget` válido). `suppressDirtyGuardRef` é levantado durante o
  // submit aceito para o close subsequente passar direto.
  const initialSnapshotRef = useRef<string | null>(null);
  const suppressDirtyGuardRef = useRef(false);
  const isDirtyRef = useRef(false);

  // Função estável: lê `isDirtyRef.current` no momento da chamada (não
  // congelada via closure). `useModalA11y` recebe ela como `onClose` e
  // dispara em Esc/click-fora. O guard só pergunta se há mudanças e o
  // submit recente não pediu para suprimir.
  function handleGuardedClose() {
    if (suppressDirtyGuardRef.current) {
      onClose();
      return;
    }
    if (isDirtyRef.current) {
      const ok =
        typeof window === "undefined"
          ? true
          : window.confirm("Você tem alterações não salvas. Descartar?");
      if (!ok) return;
    }
    onClose();
  }

  const modalRef = useModalA11y({ open: true, onClose: handleGuardedClose });
  const lastDefaultedSourceRef = useRef<string | null>(null);
  const lastAvailabilityDefaultedSourceRef = useRef<string | null>(null);
  const lastProductionDefaultedSourceRef = useRef<string | null>(null);

  const selectedInfo = useMemo(
    () => signalSources.find((s) => s.source === selectedSource),
    [selectedSource, signalSources],
  );

  // H2 — tipos compatíveis com a fonte selecionada.
  // - Em modo create, `selectedInfo` é o source escolhido no dropdown.
  // - Em modo edit, deduzimos um pseudo-source a partir do `existingWidget.source`
  //   (regex G{n}_P{m}_{s|acc|v} ou V{n}) para reaplicar a matriz.
  const compatibilitySource = useMemo<AvailableSource | undefined>(() => {
    if (selectedInfo) return selectedInfo;
    if (!existingWidget) return undefined;
    const fallback = deriveSourceFromTag(existingWidget.source);
    return fallback ? { ...fallback, signalKind: existingWidget.settings.signalKind } : undefined;
  }, [existingWidget, selectedInfo]);
  // Só entram como fonte extra as que dividem eixo com a âncora: mesma unidade
  // e mesmo `kind`. É a regra que garante que a comparação não minta sobre a
  // escala — e é a MESMA função usada pelo modelo, não uma segunda cópia.
  const combinableSources = useMemo(() => {
    if (!selectedInfo) return [];
    return combinableWith(
      { source: selectedInfo.source, unit: selectedInfo.suggestedUnit, kind: selectedInfo.signalKind },
      signalSources.map((source) => ({
        source: source.source,
        unit: source.suggestedUnit,
        kind: source.signalKind,
      })),
    ).flatMap((descriptor) => {
      const found = signalSources.find((source) => source.source === descriptor.source);
      return found ? [found] : [];
    });
  }, [selectedInfo, signalSources]);

  // Trocar a âncora invalida as extras escolhidas para a anterior. Sanear aqui
  // (e não só ao salvar) evita gravar uma combinação que a regra recusa.
  //
  // Só saneia quando dá para JULGAR. Sem `selectedInfo` — gateway fora do ar,
  // `/meta` ainda não recebido, fonte que saiu da lista — `combinableSources`
  // é vazio por ignorância, não por incompatibilidade; podar nesse caso
  // esvaziava as extras de um comparativo salvo e a validação de "pelo menos
  // uma fonte extra" travava o Salvar com um erro insolúvel na tela, mesmo
  // para quem só queria renomear o card.
  useEffect(() => {
    if (!selectedInfo) return;
    setExtraSources((current) => {
      if (current.length === 0) return current;
      const allowed = new Set(combinableSources.map((source) => source.source));
      const next = current.filter((source) => allowed.has(source));
      return next.length === current.length ? current : next;
    });
  }, [combinableSources, selectedInfo]);

  const allowedTypes = useMemo(
    () => new Set<WidgetType>(compatibleTypesFor(compatibilitySource)),
    [compatibilitySource],
  );
  const recommendedType = useMemo(
    () => recommendedTypeFor(compatibilitySource),
    [compatibilitySource],
  );
  // Familias oferecidas (Incremento B): uma familia aparece quando tem ao
  // menos um membro compativel com a fonte. `disponibilidade`/`producao`
  // consolidam pares; as demais familias tem um membro so. Substitui o antigo
  // `filteredTypeOptions` (dropdown por tipo concreto).
  const offeredFamilies = useMemo(
    () => FAMILY_OPTIONS.filter((option) => option.members.some((m) => allowedTypes.has(m))),
    [allowedTypes],
  );
  const currentFamilyOption = familyOptionOf(FAMILY_OF[widgetType]);
  const compatibleFamilyMembers = useMemo(
    () => compatibleMembers(currentFamilyOption, allowedTypes),
    [currentFamilyOption, allowedTypes],
  );
  const recommendedFamily = FAMILY_OF[recommendedType];
  // H2.4 — detecta combinação incompatível (widget existente vindo de versão
  // antiga, antes da matriz de compatibilidade). Mostramos aviso e exibimos
  // o tipo atual no dropdown como uma `<option>` fora dos grupos válidos,
  // até o operador trocar.
  const isLegacyIncompatible = !isTypeCompatible(compatibilitySource, widgetType);
  // Camada 3 da matriz: a combinação passa, mas tem ressalva. Diferente do
  // incompatível — aqui o operador pode ter razão, só precisa saber do risco.
  const discouragedNote = discouragedNoteFor(compatibilitySource, widgetType);

  // H5.3 — unidade do source na regra de atividade. Em `_v` a unidade é a
  // publicada pelo gateway (ex: L/min); em `_acc`, o threshold se aplica ao
  // delta entre amostras (unidade/leitura). Quando não der pra inferir,
  // omite o sufixo (label volta a ser só "Atividade mínima").
  const activityUnitLabel = useMemo(() => {
    if (compatibilitySource?.signal === "v" && compatibilitySource.suggestedUnit) {
      return compatibilitySource.suggestedUnit;
    }
    if (compatibilitySource?.signal === "acc") {
      return compatibilitySource.suggestedUnit
        ? `${compatibilitySource.suggestedUnit}/leitura`
        : "unidade/leitura";
    }
    return null;
  }, [compatibilitySource]);
  const isStateSignal = widgetType === "led";
  const isAvailabilityWidget = widgetType === "availability";
  const isRuleWidget = widgetType === "availability" || widgetType === "production-status";
  const isProductionKpiWidget = widgetType === "production-kpi";
  // `multi-chart` entra aqui para que serie/janela/escala sejam PERSISTIDAS —
  // sem isso o seletor de periodo no card nao teria onde gravar e voltaria
  // para 24h a cada render.
  const isChartWidget =
    widgetType === "line-chart" ||
    widgetType === "bar-chart" ||
    widgetType === "multi-chart";
  const isUtilizationWidget = widgetType === "utilization";
  // Séries válidas por sinal: `acc` produz produção-por-período ou acumulado;
  // `v` só faz sentido como ritmo. `effectiveChartSeries` corrige um valor
  // herdado incompatível com a fonte atual.
  const chartSeriesOptions: ChartSeries[] =
    compatibilitySource?.signal === "acc"
      ? ["throughput", "cumulative"]
      : compatibilitySource?.signal === "v"
        ? ["rate"]
        : ["rate", "throughput", "cumulative"];
  const effectiveChartSeries: ChartSeries = chartSeriesOptions.includes(chartSeries)
    ? chartSeries
    : chartSeriesOptions[0];
  // Alarme de parada faz sentido em widgets de movimento/estado: LED, estado de
  // produção e a linha de RITMO.
  const isStopAlarmWidget =
    widgetType === "led" ||
    widgetType === "production-status" ||
    (widgetType === "line-chart" &&
      effectiveChartSeries === "rate" &&
      compatibilitySource?.signalKind !== "instantaneous" &&
      compatibilitySource?.signalKind !== "cumulative" &&
      compatibilitySource?.signalKind !== "state");
  const isSimpleAccumulatorWidget =
    widgetType === "number" && compatibilitySource?.signal === "acc";
  const isCurrentProductionTotal =
    isProductionKpiWidget && productionMode === "total" && productionWindow === "current";
  const usesProductionTarget =
    isCurrentProductionTotal && productionTotalMode === "lot";
  const productionModes = useMemo(
    () => productionModesForSignal(compatibilitySource?.signal, compatibilitySource?.signalKind),
    [compatibilitySource?.signal, compatibilitySource?.signalKind],
  );

  // Matriz de visibilidade de campos por tipo de widget (H1.1).
  // Cada `show*` controla a renderização do campo correspondente — campos
  // que não fazem sentido para o tipo simplesmente não aparecem, em vez de
  // ficarem disabled. Isso reduz a poluição visual da tela.
  //
  // | Campo       | LED | Number | Gauge | Progress | Avail | ProdStatus | ProdKPI |
  // | ----------- | --- | ------ | ----- | -------- | ----- | ---------- | ------- |
  // | Unit        |  -  |   ✔   |  ✔   |   ✔     |   -*  |     -      |    ✔   |
  // | Min/Max     |  -  |   ✔   |  ✔   |   ✔     |   -*  |     -      |    -    |
  // | Decimais    |  -  |   ✔   |  ✔   |   ✔     |   -*  |     -      |    ✔   |
  // | Thresholds  |  -  |   ✔   |  ✔   |   ✔     |   -   |     -      |    ✔   |
  // | Estado dig  |  ✔  |   -   |  -   |   -     |   -   |     -      |    -    |
  // | Regra avail |  -  |   -   |  -   |   -     |   ✔   |     ✔      |    -    |
  // | Regra prod  |  -  |   -   |  -   |   -     |   -   |     -      |    ✔   |
  //
  // (*) Em Availability/ProdStatus, o conjunto unidade=`%`, escala=0/100,
  // decimais=0 é forçado nos defaults do efeito — então esconder o campo
  // não perde dados, e operador não digita "Max=200" achando que altera
  // alguma coisa.
  const showUnit = [
    "number",
    "gauge",
    "production-kpi",
    "line-chart",
    "bar-chart",
    "multi-value",
    "multi-chart",
    "tank",
  ].includes(widgetType);
  // Escala min/max aparece para gráficos para alimentar a escala Y "fixa".
  const showScale = [
    "number",
    "gauge",
    "line-chart",
    "bar-chart",
    "multi-chart",
    "tank",
  ].includes(widgetType);
  const showDecimals = [
    "number",
    "gauge",
    "production-kpi",
    "line-chart",
    "bar-chart",
    "multi-value",
    "multi-chart",
    "tank",
  ].includes(widgetType);
  const showThresholds = ["number", "gauge", "production-kpi", "tank"].includes(widgetType);
  // Tipos que admitem ALGUM alarme (limites/visual ou parada). Só nesses a
  // seção "Alarmes" aparece. `hasConfiguredAlarm` alimenta o selo "ativo".
  const supportsAlarms = showThresholds || isStopAlarmWidget;
  const hasConfiguredAlarm =
    (showThresholds && (warnAt.trim() !== "" || dangerAt.trim() !== "" || alarmEnabled)) ||
    (isStopAlarmWidget && stopAlarmEnabled);

  // Identidade e limites pertencem a fonte, nao ao rascunho inteiro. Trocar a
  // fonte (so no modo criar) renova os campos mesmo se foram digitados: um
  // override em metros nao prova escolha para a nova tensao. Aviso explicito,
  // sem reescrever overrides quando a fonte continua a mesma.
  useEffect(() => {
    if (isEditMode) return;
    if (!selectedInfo) return;
    if (lastDefaultedSourceRef.current === selectedInfo.source) return;
    const changedSource = lastDefaultedSourceRef.current !== null;
    lastDefaultedSourceRef.current = selectedInfo.source;
    setTitle(selectedInfo.label);
    titleEditedRef.current = false;
    setUnit(selectedInfo.suggestedUnit ?? "");
    setMin(String(selectedInfo.suggestedMin ?? 0));
    setMax(String(selectedInfo.suggestedMax ?? 100));
    setDecimals(String(selectedInfo.suggestedDecimals ?? 1));
    if (changedSource) {
      setSourceDefaultsReset(true);
      setWarnAt("");
      setDangerAt("");
      setAlarmEnabled(false);
      setStopAlarmEnabled(false);
      setAlarmsOpen(false);
      setMinActiveValue("0");
      setProductionTarget("");
    }
    setWidgetType(recommendedTypeFor(selectedInfo));
  }, [isEditMode, selectedInfo]);

  useEffect(() => {
    if (!isRuleWidget) return;
    const defaultKey = `${selectedSource}:${widgetType}`;
    if (lastAvailabilityDefaultedSourceRef.current === defaultKey) return;
    lastAvailabilityDefaultedSourceRef.current = defaultKey;
    setUnit(isAvailabilityWidget ? "%" : "");
    setMin("0");
    setMax(isAvailabilityWidget ? "100" : "1");
    setDecimals("0");
    const knownLabels = new Set(signalSources.map((source) => source.label));
    const ruleTitle = isAvailabilityWidget
      ? "Disponibilidade"
      : selectedInfo?.signal === "s"
        ? "Status de produção"
        : "Status de movimento";
    setTitle((current) =>
      current.trim() === "" ||
      current.trim().startsWith("Disponibilidade - ") ||
      current.trim().startsWith("Status de produção - ") ||
      current.trim().startsWith("Status de movimento - ") ||
      knownLabels.has(current.trim())
        ? `${ruleTitle} - ${selectedInfo?.label ?? "Sinal"}`
        : current,
    );
    if (selectedInfo?.signal === "s") {
      setAvailabilityMode("state");
    } else {
      setAvailabilityMode("activity");
    }
  }, [isAvailabilityWidget, isRuleWidget, selectedInfo, selectedSource, signalSources, widgetType]);

  useEffect(() => {
    if (!isProductionKpiWidget || productionModes.length === 0) return;
    const defaultKey = `${selectedSource}:${widgetType}`;
    if (lastProductionDefaultedSourceRef.current === defaultKey) return;
    lastProductionDefaultedSourceRef.current = defaultKey;
    // Em modo editar `sources` pode nao ser informado, portanto `selectedInfo`
    // fica vazio. A fonte derivada da tag existente continua sendo confiavel
    // e evita converter um widget `_acc` para o estado interno `hourly`.
    const defaultMode = compatibilitySource?.signal === "acc" ? "total" : "hourly";
    const preserveExistingMode =
      isEditMode && isProductionModeCompatible(compatibilitySource?.signal, productionMode, compatibilitySource?.signalKind);
    if (!preserveExistingMode) {
      setProductionMode(defaultMode);
      setProductionMultiplier(defaultMode === "hourly" ? "60" : "1");
    }
    setTitle((current) => {
      const knownLabels = new Set(signalSources.map((source) => source.label));
      const trimmed = current.trim();
      // Auto-preenche o título só quando ainda é um default/genérico — nunca
      // sobrescreve um nome amigável que o operador digitou. Usa o label do
      // sinal direto (sem o prefixo verboso "Indicador de produção - ").
      return trimmed === "" ||
        trimmed.startsWith("Produção atual - ") ||
        trimmed.startsWith("Indicador de produção - ") ||
        knownLabels.has(trimmed)
        ? selectedInfo?.label ?? "Sinal"
        : current;
    });
    setUnit((current) => current || selectedInfo?.suggestedUnit || "");
    setDecimals((current) => (defaultMode === "total" ? "0" : current || "1"));
  }, [
    compatibilitySource?.signal,
    compatibilitySource?.signalKind,
    isEditMode,
    isProductionKpiWidget,
    productionMode,
    productionModes,
    selectedInfo,
    selectedSource,
    signalSources,
    widgetType,
  ]);

  // Preview ao vivo: monta um DashboardWidget temporário a partir do
  // estado do form e resolve o valor pela função fornecida pelo pai.
  const previewWidget = useMemo<DashboardWidget | null>(() => {
    if (!selectedSource) return null;
    const parsedMin = Number(min);
    const parsedMax = Number(max);
    const parsedDecimals = Number(decimals);
    const safeSettings: WidgetSettings = {
      signalKind: selectedInfo?.signalKind ?? existingWidget?.settings.signalKind,
      renamedByOperator: titleEditedRef.current || undefined,
      chartOrigin: preservarOrigemDoGrafico(existingWidget, widgetType),
      unit: unit.trim() || undefined,
      min: Number.isFinite(parsedMin) ? parsedMin : undefined,
      max: Number.isFinite(parsedMax) ? parsedMax : undefined,
      decimals:
        Number.isInteger(parsedDecimals) && parsedDecimals >= 0 && parsedDecimals <= 6
          ? parsedDecimals
          : undefined,
      activeWhen: widgetType === "led" ? activeWhen : undefined,
      stateMappings: widgetType === "state-value" ? parseStateMappingDraft(stateRows) : undefined,
      stateLabels:
        widgetType === "led"
          ? {
              on: onLabel.trim() || "Ligado",
              off: offLabel.trim() || "Desligado",
              unavailable: unavailableLabel.trim() || "Sem sinal",
            }
          : undefined,
      thresholds: collectThresholds(
        showThresholds && alarmsOpen,
        warnAt,
        dangerAt,
        warnDirection,
        dangerDirection,
      ),
      alarmEnabled: alarmEnabled && (widgetType === "state-value" || isUtilizationWidget || alarmsOpen) ? true : undefined,
      availability: isRuleWidget
        ? {
            mode: availabilityMode,
            window: availabilityWindow,
            activeWhen: availabilityMode === "state" ? activeWhen : undefined,
            minActiveValue:
              availabilityMode === "activity" && Number.isFinite(Number(minActiveValue))
                ? Number(minActiveValue)
                : undefined,
          }
        : undefined,
      production: isProductionKpiWidget
        ? {
            mode: productionMode,
            window: productionMode === "total" ? productionWindow : undefined,
            totalMode:
              productionMode === "total" && productionWindow === "current"
                ? productionTotalMode
                : undefined,
            multiplier:
              productionMode === "hourly" && Number.isFinite(Number(productionMultiplier))
                ? Number(productionMultiplier)
                : undefined,
            target:
              (productionMode !== "total" || usesProductionTarget) &&
              Number.isFinite(Number(productionTarget)) &&
              productionTarget.trim() !== ""
                ? Number(productionTarget)
                : undefined,
          }
        : undefined,
      chart: isChartWidget
        ? {
            kind: widgetType === "bar-chart" ? "bar" : "line",
            series: effectiveChartSeries,
            window: historyWindow,
            yScale: chartYScale,
          }
        : undefined,
      history: isUtilizationWidget ? { window: historyWindow } : undefined,
      utilization: isUtilizationWidget ? utilizationSetting : undefined,
      stopAlarm:
        isStopAlarmWidget && alarmsOpen && stopAlarmEnabled
          ? { enabled: true, afterMinutes: Math.max(0, Math.round(Number(stopAlarmMinutes)) || 0) }
          : undefined,
    };
    return {
      id: existingWidget?.id ?? "__preview__",
      machineId,
      title: title.trim() || selectedInfo?.label || selectedSource,
      source: selectedSource,
      ...(requiresMultipleSources(widgetType) && extraSources.length > 0
        ? { extraSources }
        : null),
      ...(section.trim() ? { section: section.trim() } : null),
      widgetType,
      settings: safeSettings,
      // Tamanho E posicao sao do ARRASTO, nao deste formulario — ver
      // `preservarLayoutDoArrasto`.
      layout: preservarLayoutDoArrasto(
        existingWidget,
        size,
        existingWidget?.layout.order ?? nextOrder,
      ),
    };
  }, [
    activeWhen,
    alarmEnabled,
    availabilityMode,
    availabilityWindow,
    dangerAt,
    dangerDirection,
    decimals,
    existingWidget,
    machineId,
    max,
    min,
    minActiveValue,
    isRuleWidget,
    isProductionKpiWidget,
    isChartWidget,
    isUtilizationWidget,
    utilizationSetting,
    isStopAlarmWidget,
    stopAlarmEnabled,
    stopAlarmMinutes,
    effectiveChartSeries,
    extraSources,
    section,
    chartYScale,
    historyWindow,
    nextOrder,
    offLabel,
    stateRows,
    onLabel,
    selectedInfo,
    selectedSource,
    size,
    alarmsOpen,
    productionMode,
    productionTotalMode,
    productionWindow,
    productionMultiplier,
    productionTarget,
    usesProductionTarget,
    showThresholds,
    title,
    unavailableLabel,
    unit,
    warnAt,
    warnDirection,
    widgetType,
  ]);

  // H1.3 — atualiza o ref de dirty a cada mudança no preview.
  // Na primeira renderização com `previewWidget` válido, congela o JSON
  // inicial. Depois, qualquer divergência marca `isDirty`.
  useEffect(() => {
    if (!previewWidget) return;
    const serialized = JSON.stringify(previewWidget);
    if (initialSnapshotRef.current === null) {
      initialSnapshotRef.current = serialized;
      isDirtyRef.current = false;
      return;
    }
    isDirtyRef.current = initialSnapshotRef.current !== serialized;
  }, [previewWidget]);

  // H3 — faixa do slider derivada do tipo. Cobre 0..max para Number/Gauge/
  // Progress; para Production-KPI sem target, cobre 0..(rate*60); com target,
  // cobre 0..(target*1.2). Para tipos sem slider, mantém uma faixa default.
  const simRange = useMemo<{ min: number; max: number; step: number }>(() => {
    if (widgetType === "production-kpi") {
      const target = Number(productionTarget);
      const explicitMax = Number.isFinite(target) && target > 0 ? target * 1.2 : 120;
      return { min: 0, max: explicitMax, step: Math.max(1, Math.round(explicitMax / 100)) };
    }
    const parsedMin = Number(min);
    const parsedMax = Number(max);
    const safeMin = Number.isFinite(parsedMin) ? parsedMin : 0;
    const safeMax = Number.isFinite(parsedMax) && parsedMax > safeMin ? parsedMax : safeMin + 100;
    return { min: safeMin, max: safeMax, step: Math.max(0.1, (safeMax - safeMin) / 200) };
  }, [widgetType, min, max, productionTarget]);

  // Recentraliza o slider quando a faixa muda, mas só se o valor atual ficar
  // fora dos novos limites (preserva edição do operador o quanto puder).
  useEffect(() => {
    setSimNumeric((current) => {
      if (current < simRange.min || current > simRange.max) {
        return Math.round(((simRange.min + simRange.max) / 2) * 10) / 10;
      }
      return current;
    });
  }, [simRange.min, simRange.max]);

  const previewValue = useMemo<ResolvedWidgetValue | null>(() => {
    if (!previewWidget) return null;
    if (previewMode === "simulate") {
      return makeSimulatedValue(previewWidget, {
        numeric: simNumeric,
        boolState: simBoolState,
        isAvailable: simIsAvailable,
      });
    }
    if (resolveLiveValue) {
      const live = resolveLiveValue(previewWidget.source);
      if (live?.source === previewWidget.source) return live;
    }
    return makeMissingPreviewValue(previewWidget.source);
  }, [
    previewWidget,
    previewMode,
    resolveLiveValue,
    simNumeric,
    simBoolState,
    simIsAvailable,
  ]);

  // Dados sintéticos só existem no modo explícito Simular. O modal não
  // consulta histórico: nunca transformar uma leitura atual em série medida.
  const previewNeedsHistory = !!previewWidget && (
    previewWidget.widgetType === "utilization" ||
    previewWidget.widgetType === "line-chart" ||
    previewWidget.widgetType === "bar-chart" ||
    previewWidget.widgetType === "multi-chart" ||
    !!historicalProductionWindow(previewWidget)
  );
  // Um total acumulado isolado não prova atividade. Só o painel acompanha
  // amostras consecutivas e valida o delta (inclusive aquecimento/reset).
  const previewNeedsAccumulatorDelta = !!previewWidget && (
    previewWidget.widgetType === "availability" || previewWidget.widgetType === "production-status"
  ) && previewWidget.settings.availability?.mode === "activity" &&
    parseTag(previewWidget.source)?.signal === "acc";
  const previewNeedsMeasuredData = previewNeedsHistory || previewNeedsAccumulatorDelta;
  const previewHistory = useMemo<WidgetHistoryState | undefined>(
    () => (previewMode === "simulate" && previewWidget && simIsAvailable
      ? makeSamplePreviewHistory(previewWidget) : undefined),
    [previewMode, previewWidget, simIsAvailable],
  );
  const previewValues = useMemo<ResolvedWidgetValue[] | undefined>(() => {
    if (!previewWidget || !previewValue) return undefined;
    return widgetSources(previewWidget).map((source, index) => {
      if (index === 0) return previewValue;
      const sourceWidget = { ...previewWidget, source };
      if (previewMode === "simulate") {
        return makeSimulatedValue(sourceWidget, {
          numeric: simNumeric,
          boolState: simBoolState,
          isAvailable: simIsAvailable,
        });
      }
      const live = resolveLiveValue?.(source);
      return live?.source === source ? live : makeMissingPreviewValue(source);
    });
  }, [
    previewMode,
    previewValue,
    previewWidget,
    resolveLiveValue,
    simBoolState,
    simIsAvailable,
    simNumeric,
  ]);
  const previewHistories = useMemo<Array<WidgetHistoryState | undefined> | undefined>(
    () =>
      previewMode === "simulate" && previewWidget && simIsAvailable
        ? widgetSources(previewWidget).map((source, index) =>
            makeSamplePreviewHistory({ ...previewWidget, source }, index),
          )
        : undefined,
    [previewMode, previewWidget, simIsAvailable],
  );
  const previewSourceLabels = useMemo(
    () =>
      Object.fromEntries(
        signalSources.map((source) => [
          source.source,
          source.shortLabel ?? source.label,
        ]),
      ),
    [signalSources],
  );
  // A disponibilidade da simulação nunca representa a janela real do device.
  const previewAvailability = useMemo<WidgetAvailabilityState | undefined>(
    () => (previewMode === "simulate" && previewWidget && simIsAvailable
      ? makeSamplePreviewAvailability(previewWidget) : undefined),
    [previewMode, previewWidget, simIsAvailable],
  );

  // H3.3 — em modo "live" com `resolveLiveValue` que retornou um valor
  // marcado como indisponível, sinaliza pro operador que ele pode usar
  // o simulador para visualizar como o card vai aparecer.
  const liveValueIsStale =
    previewMode === "live" &&
    previewValue !== null &&
    !previewValue.isAvailable;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    // Acumula erros por campo antes de decidir abortar — assim o operador
    // vê todos os campos com problema de uma vez (em vez de ter que
    // corrigir um, dar submit, corrigir outro, dar submit, etc.).
    const errors: Record<string, string> = {};

    if (!selectedSource) {
      errors.source = "Selecione uma fonte.";
    }
    if (!title.trim()) {
      errors.title = "Informe um nome.";
    }
    if (isLegacyIncompatible) {
      errors.widgetType =
        "Tipo incompatível com a fonte. Escolha um da lista de tipos válidos.";
    }

    const parsedMin = Number(min);
    const parsedMax = Number(max);
    const parsedDecimals = Number(decimals);
    if (showScale) {
      if (!Number.isFinite(parsedMin)) errors.min = "Informe um número.";
      if (!Number.isFinite(parsedMax)) errors.max = "Informe um número.";
      if (
        !errors.min &&
        !errors.max &&
        Number.isFinite(parsedMin) &&
        Number.isFinite(parsedMax) &&
        parsedMax <= parsedMin
      ) {
        errors.max = "Máximo precisa ser maior que mínimo.";
      }
    }
    if (showDecimals) {
      if (
        !Number.isInteger(parsedDecimals) ||
        parsedDecimals < 0 ||
        parsedDecimals > 6
      ) {
        errors.decimals = "Inteiro entre 0 e 6.";
      }
    }

    let thresholds: WidgetThreshold[] | undefined;
    if (showThresholds && alarmsOpen) {
      const warn = parseOptionalNumber(warnAt);
      const danger = parseOptionalNumber(dangerAt);
      if (warn === "invalid") errors.warnAt = "Informe um número (ou deixe em branco).";
      if (danger === "invalid") errors.dangerAt = "Informe um número (ou deixe em branco).";
      if (
        warn !== null &&
        warn !== "invalid" &&
        danger !== null &&
        danger !== "invalid" &&
        warnDirection === "above" &&
        dangerDirection === "above" &&
        danger <= warn
      ) {
        errors.dangerAt = "Perigo precisa ser maior que atenção.";
      }
      if (
        warn !== null &&
        warn !== "invalid" &&
        danger !== null &&
        danger !== "invalid" &&
        warnDirection === "below" &&
        dangerDirection === "below" &&
        danger >= warn
      ) {
        errors.dangerAt = "Perigo precisa ser menor que atenção.";
      }
      if (!errors.warnAt && !errors.dangerAt) {
        thresholds = collectThresholds(true, warnAt, dangerAt, warnDirection, dangerDirection);
      }
    }

    const activeThreshold = Number(minActiveValue);
    if (isRuleWidget && availabilityMode === "activity" && !Number.isFinite(activeThreshold)) {
      errors.minActiveValue = "Informe um número.";
    }

    const parsedProductionMultiplier = Number(productionMultiplier);
    const parsedProductionTarget =
      productionTarget.trim() === "" ? null : Number(productionTarget);
    if (
      isProductionKpiWidget &&
      !isProductionModeCompatible(compatibilitySource?.signal, productionMode, compatibilitySource?.signalKind)
    ) {
      errors.productionMode = "Escolha uma métrica compatível com a fonte.";
    }
    if (
      isProductionKpiWidget &&
      productionMode === "hourly" &&
      (!Number.isFinite(parsedProductionMultiplier) || parsedProductionMultiplier <= 0)
    ) {
      errors.productionMultiplier = "Precisa ser maior que zero.";
    }
    if (isProductionKpiWidget && usesProductionTarget && parsedProductionTarget === null) {
      errors.productionTarget = "Informe o limite do lote.";
    } else if (
      isProductionKpiWidget &&
      (productionMode !== "total" || usesProductionTarget) &&
      parsedProductionTarget !== null &&
      (!Number.isFinite(parsedProductionTarget) || parsedProductionTarget <= 0)
    ) {
      errors.productionTarget = usesProductionTarget
        ? "O limite precisa ser maior que zero."
        : "Maior que zero ou em branco.";
    }

    // Um comparativo com uma fonte só é um número grande com nome errado. O
    // editor recusa em vez de salvar um card que nasce degradado.
    if (requiresMultipleSources(widgetType) && extraSources.length === 0) {
      errors.extraSources =
        "Escolha ao menos mais uma fonte para comparar, ou troque o tipo do widget.";
    }

    if (widgetType === "state-value" && !parseStateMappingDraft(stateRows)) {
      errors.stateMappings = "Defina de 1 a 16 códigos numéricos únicos, com texto e cor. Não deixe campos vazios.";
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    // Sucesso: o pai vai fechar o modal. Suprime o guard de descarte para
    // o close subsequente passar direto (H1.3).
    suppressDirtyGuardRef.current = true;

    const widget: DashboardWidget = {
      id: existingWidget?.id ?? `widget-${selectedSource}-${Date.now().toString(36)}`,
      machineId,
      title: title.trim(),
      source: selectedSource,
      ...(requiresMultipleSources(widgetType) && extraSources.length > 0
        ? { extraSources }
        : null),
      ...(section.trim() ? { section: section.trim() } : null),
      widgetType,
      settings: {
        signalKind: selectedInfo?.signalKind ?? existingWidget?.settings.signalKind,
        renamedByOperator: titleEditedRef.current || undefined,
        chartOrigin: preservarOrigemDoGrafico(existingWidget, widgetType),
        unit: unit.trim() || undefined,
        min: parsedMin,
        max: parsedMax,
        decimals: parsedDecimals,
        thresholds,
        activeWhen: isStateSignal ? activeWhen : undefined,
        stateMappings: widgetType === "state-value" ? parseStateMappingDraft(stateRows) : undefined,
        stateLabels: isStateSignal
          ? {
              on: onLabel.trim() || "Ligado",
              off: offLabel.trim() || "Desligado",
              unavailable: unavailableLabel.trim() || "Sem sinal",
            }
          : undefined,
        alarmEnabled: alarmEnabled && (widgetType === "state-value" || isUtilizationWidget || alarmsOpen) ? true : undefined,
        availability: isRuleWidget
          ? {
              mode: availabilityMode,
              window: availabilityWindow,
              activeWhen: availabilityMode === "state" ? activeWhen : undefined,
              minActiveValue: availabilityMode === "activity" ? activeThreshold : undefined,
            }
          : undefined,
        production: isProductionKpiWidget
          ? {
              mode: productionMode,
              window: productionMode === "total" ? productionWindow : undefined,
              totalMode:
                productionMode === "total" && productionWindow === "current"
                  ? productionTotalMode
                  : undefined,
              multiplier:
                productionMode === "hourly" ? parsedProductionMultiplier : undefined,
              target:
                productionMode !== "total" || usesProductionTarget
                  ? parsedProductionTarget ?? undefined
                  : undefined,
            }
          : undefined,
        chart: isChartWidget
        ? {
            kind: widgetType === "bar-chart" ? "bar" : "line",
            series: effectiveChartSeries,
            window: historyWindow,
            yScale: chartYScale,
          }
        : undefined,
      history: isUtilizationWidget ? { window: historyWindow } : undefined,
      utilization: isUtilizationWidget ? utilizationSetting : undefined,
      stopAlarm:
        isStopAlarmWidget && alarmsOpen && stopAlarmEnabled
          ? { enabled: true, afterMinutes: Math.max(0, Math.round(Number(stopAlarmMinutes)) || 0) }
          : undefined,
      },
      // Tamanho E posicao sao do ARRASTO, nao deste formulario — ver
      // `preservarLayoutDoArrasto`.
      layout: preservarLayoutDoArrasto(
        existingWidget,
        size,
        existingWidget?.layout.order ?? nextOrder,
      ),
    };

    onSave(widget);
  }

  // Estado vazio: criar sem fontes disponíveis.
  if (!isEditMode && signalSources.length === 0) {
    return (
      <div
        className="modal-backdrop"
        role="presentation"
        onClick={(event) => {
          if (event.target === event.currentTarget) onClose();
        }}
      >
        <div
          className="modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="widget-editor-title"
          ref={modalRef}
          tabIndex={-1}
        >
          <header className="modal__header">
            <div>
              <p className="page__eyebrow">Novo widget</p>
              <h2 id="widget-editor-title">Adicionar widget</h2>
            </div>
            <button type="button" className="btn ghost sm" onClick={onClose}>
              Fechar
            </button>
          </header>
          <div className="modal__body">
            <p className="muted">
              Nenhuma fonte real disponível ainda. Associe um gateway via{" "}
              <strong>Adicionar dispositivo</strong> para criar widgets operacionais.
            </p>
            <div className="modal__actions">
              <button type="button" className="btn primary" onClick={onClose}>
                OK
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) handleGuardedClose();
      }}
    >
      <div
        className="modal modal--wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="widget-editor-title"
        ref={modalRef}
        tabIndex={-1}
      >
        <header className="modal__header">
          <div>
            <p className="page__eyebrow">{isEditMode ? "Editar widget" : "Novo widget"}</p>
            <h2 id="widget-editor-title">
              {isEditMode ? `Editar ${existingWidget!.title}` : "Adicionar widget"}
            </h2>
            <p className="muted mono" style={{ margin: "4px 0 0", fontSize: 12 }}>
              {selectedSource || "—"}
            </p>
          </div>
          <button type="button" className="btn ghost sm" onClick={handleGuardedClose} aria-label="Fechar">
            Fechar
          </button>
        </header>

        <form className="modal__split" onSubmit={handleSubmit}>
          <div className="modal__split-form">
            {!isEditMode ? (
              <div className="field">
                <label className="field__label" htmlFor="widget-editor-source">
                  Fonte base
                </label>
                <select
                  id="widget-editor-source"
                  className="input field__control"
                  value={selectedSource}
                  onChange={(event) => {
                    setSelectedSource(event.target.value);
                    // Atualiza tipo no mesmo render para um efeito do tipo
                    // anterior nao recolocar unidade derivada (% etc.).
                    setWidgetType(recommendedTypeFor(signalSources.find((source) => source.source === event.target.value)));
                    setStateRows([{ value: "0", label: "", tone: "neutral" }, { value: "1", label: "", tone: "neutral" }]);
                  }}
                >
                  {renderSourceOptions(signalSources)}
                </select>
                {selectedInfo ? (
                  <small className="muted mono" style={{ fontSize: 11 }}>
                    Sinal real — {selectedInfo.mode ?? "modo?"}
                    {selectedInfo.suggestedUnit ? ` · ${selectedInfo.suggestedUnit}` : ""}
                  </small>
                ) : null}
                <FieldError message={fieldErrors.source} />
                {sourceDefaultsReset ? (
                  <p className="hint" role="status">
                    Fonte alterada: nome, unidade, escala, precisão, alarmes, limite de atividade e meta foram redefinidos.
                    Revise a configuração da nova fonte antes de salvar.
                  </p>
                ) : null}
              </div>
            ) : null}

            {requiresMultipleSources(widgetType) ? (
              <div className="field">
                <span className="field__label">Fontes para comparar</span>
                {combinableSources.length === 0 ? (
                  <small className="muted" style={{ fontSize: 11 }}>
                    Nenhuma outra fonte com a mesma unidade e o mesmo tipo de
                    grandeza. Um comparativo precisa de grandezas que dividam o
                    mesmo eixo.
                  </small>
                ) : (
                  <>
                    <small className="muted" style={{ fontSize: 11, display: "block", marginBottom: 6 }}>
                      Só aparecem fontes que dividem eixo com a base — mesma
                      unidade e mesmo tipo de grandeza.
                    </small>
                    <div className="w-multi__picker">
                      {combinableSources.map((source) => {
                        const checked = extraSources.includes(source.source);
                        return (
                          <label key={source.source} className="w-multi__picker-item">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(event) => {
                                setExtraSources((current) =>
                                  event.target.checked
                                    ? [...current, source.source]
                                    : current.filter((item) => item !== source.source),
                                );
                              }}
                            />
                            <span>{source.label}</span>
                            {source.suggestedUnit ? (
                              <span className="muted mono" style={{ fontSize: 10 }}>
                                {source.suggestedUnit}
                              </span>
                            ) : null}
                          </label>
                        );
                      })}
                    </div>
                  </>
                )}
                <FieldError message={fieldErrors.extraSources} />
              </div>
            ) : null}

            <div className={showUnit ? "form-grid form-grid--2" : "form-grid"}>
              <div className="field">
                <label className="field__label" htmlFor="widget-editor-type">
                  Tipo de widget
                </label>
                <select
                  id="widget-editor-type"
                  className="input field__control"
                  value={isLegacyIncompatible ? widgetType : FAMILY_OF[widgetType]}
                  onChange={(event) => {
                    const value = event.target.value;
                    const family = FAMILY_OPTIONS.find((option) => option.family === value);
                    if (family) {
                      setWidgetType(resolveFamilyMember(family, allowedTypes, widgetType));
                    } else {
                      // Opção legada crua (combinação atual incompatível).
                      setWidgetType(value as WidgetType);
                    }
                  }}
                  aria-invalid={isLegacyIncompatible ? true : undefined}
                >
                  {(["reading", "analysis"] as const).map((category) => {
                    const familiesInGroup = offeredFamilies.filter(
                      (option) => option.category === category,
                    );
                    if (familiesInGroup.length === 0) return null;
                    return (
                      <optgroup key={category} label={CATEGORY_LABELS[category]}>
                        {familiesInGroup.map((option) => (
                          <option key={option.family} value={option.family}>
                            {option.label}
                            {option.family === recommendedFamily ? " — recomendado" : ""}
                          </option>
                        ))}
                      </optgroup>
                    );
                  })}
                  {isLegacyIncompatible ? (
                    <optgroup label="Combinação atual (precisa ser trocada)">
                      <option value={widgetType}>
                        {TYPE_OPTIONS.find((o) => o.value === widgetType)?.label ?? widgetType}
                      </option>
                    </optgroup>
                  ) : null}
                </select>
                <small className="muted" style={{ fontSize: 11 }}>
                  {isLegacyIncompatible
                    ? TYPE_OPTIONS.find((o) => o.value === widgetType)?.hint
                    : currentFamilyOption.hint}
                </small>
                {isLegacyIncompatible ? (
                  <FieldError
                    message={
                      "Esta combinação tipo × fonte foi desativada. Escolha um tipo da lista para continuar."
                    }
                  />
                ) : null}
                {/* Camada 3: passa, mas com ressalva. Não é erro — o operador
                    pode ter contexto que o sistema não tem, e travá-lo aqui
                    seria o extremo oposto do defeito que a matriz corrige. */}
                {!isLegacyIncompatible && discouragedNote ? (
                  <small
                    className="muted"
                    role="note"
                    style={{ fontSize: 11, display: "block", marginTop: 4 }}
                  >
                    Atenção: {discouragedNote}
                  </small>
                ) : null}
              </div>
              {showUnit ? (
                <div className="field">
                  <label className="field__label" htmlFor="widget-editor-unit">
                    Unidade
                  </label>
                  <input
                    id="widget-editor-unit"
                    className="input field__control"
                    value={unit}
                    onChange={(event) => setUnit(event.target.value)}
                    placeholder="ex: un, L, m/min"
                  />
                  <small className="muted" style={{ fontSize: 11 }}>
                    Aparece ao lado do número no card.
                  </small>
                </div>
              ) : null}
            </div>

            {!isLegacyIncompatible &&
            currentFamilyOption.subLabel &&
            compatibleFamilyMembers.length >= 2 ? (
              <label className="field">
                <span className="field__label">{currentFamilyOption.subLabel}</span>
                <select
                  className="input field__control"
                  aria-label={currentFamilyOption.subLabel}
                  value={widgetType}
                  onChange={(event) => setWidgetType(event.target.value as WidgetType)}
                >
                  {compatibleFamilyMembers.map((member) => (
                    <option key={member} value={member}>
                      {currentFamilyOption.memberLabels?.[member] ?? member}
                    </option>
                  ))}
                </select>
                {currentFamilyOption.subHint ? (
                  <small className="muted" style={{ fontSize: 11 }}>
                    {currentFamilyOption.subHint}
                  </small>
                ) : null}
              </label>
            ) : null}

            <label className="field">
              <span className="field__label">Nome amigável</span>
              <input
                className="input field__control"
                value={title}
                onChange={(event) => {
                  titleEditedRef.current = true;
                  setTitle(event.target.value);
                }}
                placeholder="Ex: Produção / hora"
                aria-invalid={fieldErrors.title ? true : undefined}
              />
              <FieldError message={fieldErrors.title} />
            </label>

            {/* Agrupar e ESCOLHA. Vazio = grade plana, como sempre foi: numa
                maquina com poucos sinais, cabecalho por toda parte so alonga
                a pagina. */}
            <label className="field">
              <span className="field__label">Seção</span>
              <input
                className="input field__control"
                list="ghm-secoes"
                value={section}
                onChange={(event) => setSection(event.target.value)}
                placeholder="Opcional — ex: Produção, Elétrico"
              />
              <datalist id="ghm-secoes">
                {knownSections.map((name) => (
                  <option key={name} value={name} />
                ))}
              </datalist>
              <small className="muted" style={{ fontSize: 11 }}>
                Cards com a mesma seção ficam sob um cabeçalho. Em branco, entram
                na grade sem agrupamento.
              </small>
            </label>

            {widgetType === "state-value" ? <>
              <StateMappingFields rows={stateRows} onChange={setStateRows} error={fieldErrors.stateMappings} />
              <label className="field">
                <span><input type="checkbox" checked={alarmEnabled} onChange={(event) => setAlarmEnabled(event.target.checked)} /> Alarme visual quando o código indicar alarme</span>
              </label>
            </> : null}

            {isSimpleAccumulatorWidget ? (
              <fieldset className="modal__fieldset">
                <legend>Zeramento e períodos</legend>
                <p className="hint" style={{ margin: 0 }}>
                  Este card ainda mostra somente o total físico do equipamento. Escolha
                  como deseja acompanhar a metragem; nada será zerado antes de salvar.
                </p>
                <div className="modal__actions" style={{ paddingTop: 0 }}>
                  <button
                    type="button"
                    className="btn ghost"
                    onClick={() => {
                      setProductionMode("total");
                      setProductionWindow("24h");
                      setProductionTotalMode("physical");
                      setWidgetType("production-kpi");
                    }}
                  >
                    Configurar total por período
                  </button>
                  <button
                    type="button"
                    className="btn primary"
                    onClick={() => {
                      setProductionMode("total");
                      setProductionWindow("current");
                      setProductionTotalMode("baseline");
                      setWidgetType("production-kpi");
                    }}
                  >
                    Configurar zeramento
                  </button>
                </div>
              </fieldset>
            ) : null}

            {isRuleWidget ? (
              <fieldset className="modal__fieldset">
                <legend>
                  {isAvailabilityWidget ? "Regra de disponibilidade" : "Regra de produção"}
                </legend>
                <div className="form-grid form-grid--3">
                  <label className="field">
                    <span className="field__label">Critério</span>
                    <select
                      className="input field__control"
                      value={availabilityMode}
                      onChange={(event) =>
                        setAvailabilityMode(event.target.value as "state" | "activity")
                      }
                    >
                      <option value="state">Por estado da entrada</option>
                      <option value="activity">Por atividade / taxa</option>
                    </select>
                  </label>
                  <label className="field">
                    <span className="field__label">Janela</span>
                    <select
                      className="input field__control"
                      value={availabilityWindow}
                      onChange={(event) =>
                        setAvailabilityWindow(
                          event.target.value as NonNullable<
                            DashboardWidget["settings"]["availability"]
                          >["window"],
                        )
                      }
                    >
                      <option value="current">Agora</option>
                      <option value="6h">Últimas 6h</option>
                      <option value="12h">Últimas 12h</option>
                      <option value="24h">Últimas 24h</option>
                      <option value="48h">Últimas 48h</option>
                      <option value="7d">Últimos 7 dias</option>
                      <option value="30d">Últimos 30 dias</option>
                    </select>
                  </label>
                  {availabilityMode === "state" ? (
                    <label className="field">
                      <span className="field__label">Disponível quando</span>
                      <select
                        className="input field__control"
                        value={activeWhen}
                        onChange={(event) => setActiveWhen(Number(event.target.value) as 0 | 1)}
                      >
                        <option value={1}>s = 1</option>
                        <option value={0}>s = 0</option>
                      </select>
                    </label>
                  ) : (
                    <label className="field">
                      <span className="field__label">
                        Atividade mínima
                        {activityUnitLabel ? ` (${activityUnitLabel})` : ""}
                      </span>
                      <input
                        className="input field__control mono"
                        value={minActiveValue}
                        onChange={(event) => setMinActiveValue(event.target.value)}
                        inputMode="decimal"
                        placeholder="Ex: 0"
                        aria-invalid={fieldErrors.minActiveValue ? true : undefined}
                      />
                      <FieldError message={fieldErrors.minActiveValue} />
                    </label>
                  )}
                </div>
                <p className="hint" style={{ margin: 0 }}>
                  Use estado para uma entrada liga/desliga. Use atividade quando a máquina
                  pode ficar ligada sem produzir e a disponibilidade depende de contagem,
                  velocidade ou vazão.
                </p>
              </fieldset>
            ) : null}

            {isProductionKpiWidget ? (
              <fieldset className="modal__fieldset">
                <legend>Regra de produção</legend>
                <div className="form-grid form-grid--3">
                  <label className="field">
                    <span className="field__label">Métrica</span>
                    <select
                      className="input field__control"
                      value={productionMode}
                      onChange={(event) => setProductionMode(event.target.value as ProductionMode)}
                      aria-invalid={fieldErrors.productionMode ? true : undefined}
                    >
                      {PRODUCTION_MODE_OPTIONS.filter((option) =>
                        productionModes.includes(option.value),
                      ).map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <FieldError message={fieldErrors.productionMode} />
                  </label>
                  {productionMode === "hourly" ? (
                    <label className="field">
                      <span className="field__label">Multiplicador</span>
                      <input
                        className="input field__control mono"
                        value={productionMultiplier}
                        onChange={(event) => setProductionMultiplier(event.target.value)}
                        inputMode="decimal"
                        aria-invalid={fieldErrors.productionMultiplier ? true : undefined}
                      />
                      <FieldError message={fieldErrors.productionMultiplier} />
                    </label>
                  ) : null}
                  {productionMode !== "total" ? (
                    <label className="field">
                      <span className="field__label">Meta opcional</span>
                      <input
                        className="input field__control mono"
                        value={productionTarget}
                        onChange={(event) => setProductionTarget(event.target.value)}
                        inputMode="decimal"
                        placeholder="Ex: 1200"
                        aria-invalid={fieldErrors.productionTarget ? true : undefined}
                      />
                      <FieldError message={fieldErrors.productionTarget} />
                    </label>
                  ) : null}
                  {productionMode === "total" ? (
                    <label className="field">
                      <span className="field__label">Período do total</span>
                      <select
                        className="input field__control"
                        value={productionWindow}
                        onChange={(event) =>
                          setProductionWindow(
                            event.target.value as NonNullable<
                              WidgetSettings["production"]
                            >["window"],
                          )
                        }
                      >
                        <option value="current">Atual</option>
                        <option value="6h">Últimas 6 horas</option>
                        <option value="12h">Últimas 12 horas</option>
                        <option value="24h">Últimas 24 horas</option>
                        <option value="48h">Últimas 48 horas</option>
                        <option value="7d">Últimos 7 dias</option>
                        <option value="30d">Últimos 30 dias</option>
                      </select>
                    </label>
                  ) : null}
                  {isCurrentProductionTotal ? (
                    <label className="field">
                      <span className="field__label">Controle da metragem</span>
                      <select
                        className="input field__control"
                        value={productionTotalMode}
                        onChange={(event) =>
                          setProductionTotalMode(event.target.value as ProductionTotalMode)
                        }
                      >
                        <option value="physical">Total físico do equipamento</option>
                        <option value="baseline">Desde o último zeramento</option>
                        <option value="lot">Lote com meta e zeramento</option>
                      </select>
                    </label>
                  ) : null}
                  {usesProductionTarget ? (
                    <label className="field">
                      <span className="field__label">
                        Limite do lote{unit.trim() ? ` (${unit.trim()})` : ""}
                      </span>
                      <input
                        className="input field__control mono"
                        value={productionTarget}
                        onChange={(event) => setProductionTarget(event.target.value)}
                        inputMode="decimal"
                        placeholder="Ex: 300"
                        aria-invalid={fieldErrors.productionTarget ? true : undefined}
                      />
                      <FieldError message={fieldErrors.productionTarget} />
                    </label>
                  ) : null}
                </div>
                <p className="hint" style={{ margin: 0 }}>
                  O total físico vem do ESP. O modo desde o zeramento cria uma referência
                  analítica no servidor sem exigir meta; o lote acrescenta fechamento
                  automático por limite. Nenhuma opção apaga o acumulado do equipamento.
                </p>
              </fieldset>
            ) : null}

            {isChartWidget ? (
              <fieldset className="modal__fieldset">
                <legend>Gráfico</legend>
                {chartSeriesOptions.length > 1 ? (
                  <label className="field">
                    <span className="field__label">Série</span>
                    <select
                      className="input field__control"
                      value={effectiveChartSeries}
                      onChange={(event) =>
                        setChartSeries(event.target.value as ChartSeries)
                      }
                    >
                      {chartSeriesOptions.map((option) => (
                        <option key={option} value={option}>
                          {chartSeriesLabel(option, compatibilitySource?.signalKind)}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                <label className="field">
                  <span className="field__label">Janela do gráfico (eixo X)</span>
                  <select
                    className="input field__control"
                    value={historyWindow}
                    onChange={(event) =>
                      setHistoryWindow(
                        event.target.value as NonNullable<WidgetSettings["history"]>["window"],
                      )
                    }
                  >
                    <option value="6h">Últimas 6 horas</option>
                    <option value="12h">Últimas 12 horas</option>
                    <option value="24h">Últimas 24 horas</option>
                    <option value="48h">Últimas 48 horas</option>
                    <option value="7d">Últimos 7 dias</option>
                    <option value="30d">Últimos 30 dias</option>
                  </select>
                </label>
                <label className="field">
                  <span className="field__label">Escala vertical (eixo Y)</span>
                  <select
                    className="input field__control"
                    value={chartYScale}
                    onChange={(event) =>
                      setChartYScale(event.target.value as "auto" | "fixed")
                    }
                  >
                    <option value="auto">Automática (ajusta aos dados)</option>
                    <option value="fixed">Fixa (usa mínimo e máximo)</option>
                  </select>
                </label>
                <p className="hint" style={{ margin: 0 }}>
                  O gráfico usa a mesma série Timescale dos indicadores históricos.
                  {chartYScale === "fixed"
                    ? " Defina mínimo e máximo em “Mais opções de exibição”."
                    : ""}
                </p>
              </fieldset>
            ) : null}

            {isUtilizationWidget ? (
              <fieldset className="modal__fieldset">
                <legend>Disponibilidade</legend>
                <label className="field">
                  <span className="field__label">Janela</span>
                  <select
                    className="input field__control"
                    value={historyWindow}
                    onChange={(event) =>
                      setHistoryWindow(
                        event.target.value as NonNullable<WidgetSettings["history"]>["window"],
                      )
                    }
                  >
                    <option value="6h">Últimas 6 horas</option>
                    <option value="12h">Últimas 12 horas</option>
                    <option value="24h">Últimas 24 horas</option>
                    <option value="48h">Últimas 48 horas</option>
                    <option value="7d">Últimos 7 dias</option>
                    <option value="30d">Últimos 30 dias</option>
                  </select>
                </label>

                {/* Cortes por processo: o que é boa/média/baixa utilização. */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <label className="field">
                    <span className="field__label">Boa a partir de (%)</span>
                    <input
                      className="input field__control mono"
                      value={utilGood}
                      onChange={(event) => setUtilGood(event.target.value)}
                      placeholder="Ex: 75"
                      inputMode="numeric"
                    />
                    <small className="muted" style={{ fontSize: 11 }}>
                      Verde a partir daqui.
                    </small>
                  </label>
                  <label className="field">
                    <span className="field__label">Atenção abaixo de (%)</span>
                    <input
                      className="input field__control mono"
                      value={utilWarn}
                      onChange={(event) => setUtilWarn(event.target.value)}
                      placeholder="Ex: 50"
                      inputMode="numeric"
                    />
                    <small className="muted" style={{ fontSize: 11 }}>
                      Vermelho abaixo daqui; amarelo entre os dois.
                    </small>
                  </label>
                </div>

                <label
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: 12,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={alarmEnabled}
                    onChange={(event) => setAlarmEnabled(event.target.checked)}
                    style={{ width: 14, height: 14 }}
                  />
                  <span>
                    Disparar <strong>alarme visual</strong> (borda pulsante) quando a utilização ficar baixa
                  </span>
                </label>

                <p className="hint" style={{ margin: 0 }}>
                  Mede tempo rodando × parado e a utilização de movimento na
                  janela, a partir da telemetria Timescale.
                </p>
              </fieldset>
            ) : null}

            {(showScale || showDecimals) ? (
              <fieldset className="modal__fieldset">
                <legend>
                  Mais opções de exibição
                  <small className="muted" style={{ fontWeight: 400, marginLeft: 6 }}>
                    (escala, decimais)
                  </small>
                </legend>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {showScale || showDecimals ? (
                    <div
                      className={
                        showScale && showDecimals
                          ? "form-grid form-grid--3"
                          : showScale
                            ? "form-grid form-grid--2"
                            : "form-grid"
                      }
                    >
                      {showScale ? (
                        <div className="field">
                          <label className="field__label" htmlFor="widget-editor-min">
                            Mínimo
                          </label>
                          <input
                            id="widget-editor-min"
                            className="input field__control mono"
                            value={min}
                            onChange={(event) => setMin(event.target.value)}
                            inputMode="decimal"
                            aria-invalid={fieldErrors.min ? true : undefined}
                          />
                          <FieldError message={fieldErrors.min} />
                          <small className="muted" style={{ fontSize: 11 }}>
                            Início da escala do gauge / barra.
                          </small>
                        </div>
                      ) : null}
                      {showScale ? (
                        <div className="field">
                          <label className="field__label" htmlFor="widget-editor-max">
                            Máximo
                          </label>
                          <input
                            id="widget-editor-max"
                            className="input field__control mono"
                            value={max}
                            onChange={(event) => setMax(event.target.value)}
                            inputMode="decimal"
                            aria-invalid={fieldErrors.max ? true : undefined}
                          />
                          <FieldError message={fieldErrors.max} />
                          <small className="muted" style={{ fontSize: 11 }}>
                            Fim da escala. Define o ponteiro cheio.
                          </small>
                        </div>
                      ) : null}
                      {showDecimals ? (
                        <div className="field">
                          <label className="field__label" htmlFor="widget-editor-decimals">
                            Decimais
                          </label>
                          <input
                            id="widget-editor-decimals"
                            className="input field__control mono"
                            value={decimals}
                            onChange={(event) => setDecimals(event.target.value)}
                            inputMode="numeric"
                            aria-invalid={fieldErrors.decimals ? true : undefined}
                          />
                          <FieldError message={fieldErrors.decimals} />
                          <small className="muted" style={{ fontSize: 11 }}>
                            Casas após a vírgula (0 a 6).
                          </small>
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                </div>
              </fieldset>
            ) : null}

            {supportsAlarms ? (
              <fieldset className="modal__fieldset">
                <legend>
                  Alarmes
                  {alarmsOpen && hasConfiguredAlarm ? (
                    <span className="badge muted" style={{ marginLeft: 6 }}>
                      ativo
                    </span>
                  ) : null}
                </legend>

                {!alarmsOpen ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <p className="hint" style={{ margin: 0 }}>
                      Opcional. Por padrão o card não tem alarme. Adicione se quiser
                      que ele mude de cor ou avise em condições específicas.
                    </p>
                    <button
                      type="button"
                      className="btn ghost sm"
                      style={{ alignSelf: "flex-start" }}
                      onClick={() => setAlarmsOpen(true)}
                    >
                      + Adicionar alarme
                    </button>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    {showThresholds ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        <div className="form-grid form-grid--2">
                          <div className="field">
                            <label className="field__label" htmlFor="widget-editor-warn">
                              Atenção (warn)
                            </label>
                            <input
                              id="widget-editor-warn"
                              className="input field__control mono"
                              value={warnAt}
                              onChange={(event) => setWarnAt(event.target.value)}
                              placeholder="Ex: 80"
                              inputMode="decimal"
                              aria-invalid={fieldErrors.warnAt ? true : undefined}
                            />
                            <select
                              className="input field__control"
                              value={warnDirection}
                              onChange={(event) =>
                                setWarnDirection(event.target.value === "below" ? "below" : "above")
                              }
                              aria-label="Direcao do limite de atencao"
                            >
                              <option value="above">Dispara acima/igual</option>
                              <option value="below">Dispara abaixo/igual</option>
                            </select>
                            <FieldError message={fieldErrors.warnAt} />
                            <small className="muted" style={{ fontSize: 11 }}>
                              Card fica amarelo conforme a direção escolhida.
                            </small>
                          </div>
                          <div className="field">
                            <label className="field__label" htmlFor="widget-editor-danger">
                              Perigo (danger)
                            </label>
                            <input
                              id="widget-editor-danger"
                              className="input field__control mono"
                              value={dangerAt}
                              onChange={(event) => setDangerAt(event.target.value)}
                              placeholder="Ex: 95"
                              inputMode="decimal"
                              aria-invalid={fieldErrors.dangerAt ? true : undefined}
                            />
                            <select
                              className="input field__control"
                              value={dangerDirection}
                              onChange={(event) =>
                                setDangerDirection(event.target.value === "below" ? "below" : "above")
                              }
                              aria-label="Direcao do limite de perigo"
                            >
                              <option value="above">Dispara acima/igual</option>
                              <option value="below">Dispara abaixo/igual</option>
                            </select>
                            <FieldError message={fieldErrors.dangerAt} />
                            <small className="muted" style={{ fontSize: 11 }}>
                              Card fica vermelho conforme a direção escolhida.
                            </small>
                          </div>
                        </div>
                        <label
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            fontSize: 12,
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={alarmEnabled}
                            onChange={(event) => setAlarmEnabled(event.target.checked)}
                            style={{ width: 14, height: 14 }}
                          />
                          <span>
                            Disparar <strong>alarme visual</strong> (borda pulsante) quando
                            atingir o limite de perigo
                          </span>
                        </label>
                      </div>
                    ) : null}

                    {isStopAlarmWidget ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        <label
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            fontSize: 12,
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={stopAlarmEnabled}
                            onChange={(event) => setStopAlarmEnabled(event.target.checked)}
                            style={{ width: 14, height: 14 }}
                          />
                          <span>
                            <strong>Alarme de parada</strong> (vermelho + borda pulsante)
                            quando a máquina ficar parada
                          </span>
                        </label>
                        {stopAlarmEnabled ? (
                          <label className="field" style={{ maxWidth: 220 }}>
                            <span className="field__label">Tolerância (min)</span>
                            <input
                              className="input field__control mono"
                              value={stopAlarmMinutes}
                              onChange={(event) => setStopAlarmMinutes(event.target.value)}
                              placeholder="Ex: 2"
                              inputMode="numeric"
                            />
                            <small className="muted" style={{ fontSize: 11 }}>
                              0 = imediato; senão, alarma após esse tempo parada.
                            </small>
                          </label>
                        ) : null}
                      </div>
                    ) : null}

                    <button
                      type="button"
                      className="btn ghost sm"
                      style={{ alignSelf: "flex-start", color: "var(--danger-600)" }}
                      onClick={removeAllAlarms}
                    >
                      Remover todos os alarmes
                    </button>
                  </div>
                )}
              </fieldset>
            ) : null}

            {isStateSignal ? (
              <fieldset className="modal__fieldset">
                <legend>Estado digital</legend>
                <label className="field">
                  <span className="field__label">Ativo quando</span>
                  <select
                    className="input field__control"
                    value={activeWhen}
                    onChange={(event) => setActiveWhen(Number(event.target.value) as 0 | 1)}
                  >
                    <option value={1}>s = 1</option>
                    <option value={0}>s = 0</option>
                  </select>
                </label>
                <div className="form-grid form-grid--3">
                  <label className="field">
                    <span className="field__label">Texto ativo</span>
                    <input
                      className="input field__control"
                      value={onLabel}
                      onChange={(event) => setOnLabel(event.target.value)}
                      placeholder="Ligado"
                    />
                  </label>
                  <label className="field">
                    <span className="field__label">Texto inativo</span>
                    <input
                      className="input field__control"
                      value={offLabel}
                      onChange={(event) => setOffLabel(event.target.value)}
                      placeholder="Desligado"
                    />
                  </label>
                  <label className="field">
                    <span className="field__label">Sem sinal</span>
                    <input
                      className="input field__control"
                      value={unavailableLabel}
                      onChange={(event) => setUnavailableLabel(event.target.value)}
                      placeholder="Sem sinal"
                    />
                  </label>
                </div>
              </fieldset>
            ) : null}

            <p className="hint" style={{ margin: "4px 0 0" }}>
              O tamanho do card vem da malha: arraste o canto inferior direito
              dele no painel, em Edição. Cada tipo tem um mínimo — um gráfico
              não encolhe até virar risco.
            </p>

            {Object.keys(fieldErrors).length > 0 ? (
              <div className="inline-warning" role="status" aria-live="polite">
                Existem campos com erro. Confira as marcações em vermelho.
              </div>
            ) : null}

            {confirmingReset && existingWidget && onResetSignalConfig ? (
              <div
                className="inline-warning"
                role="alertdialog"
                aria-labelledby="reset-confirm-title"
                style={{ display: "flex", flexDirection: "column", gap: 10 }}
              >
                <strong id="reset-confirm-title">
                  Resetar configuração do sinal {existingWidget.source}?
                </strong>
                <span>Vai apagar a configuração local e restaurar os defaults do gateway:</span>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.6 }}>
                  <li>
                    Rótulo personalizado{title.trim() ? ` (atual: "${title.trim()}")` : ""}
                  </li>
                  <li>
                    Tipo escolhido (atual:{" "}
                    {TYPE_OPTIONS.find((o) => o.value === widgetType)?.label ?? widgetType})
                  </li>
                  {isStateSignal ? (
                    <li>Polaridade do LED (atual: s = {activeWhen})</li>
                  ) : null}
                  {showThresholds && (warnAt || dangerAt) ? (
                    <li>
                      Limites de alerta (atual: warn={warnAt || "—"}, danger=
                      {dangerAt || "—"})
                    </li>
                  ) : null}
                </ul>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    type="button"
                    className="btn ghost sm"
                    onClick={() => setConfirmingReset(false)}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn sm"
                    style={{ color: "var(--danger-600)" }}
                    onClick={() => {
                      setConfirmingReset(false);
                      // Suprime o guard de dirty: o reset legitimamente
                      // descarta o estado atual, e o pai vai fechar o modal.
                      suppressDirtyGuardRef.current = true;
                      onResetSignalConfig();
                    }}
                  >
                    Resetar configuração
                  </button>
                </div>
              </div>
            ) : null}

            <div
              className="modal__actions"
              style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}
            >
              <div style={{ display: "flex", gap: 8 }}>
                {isEditMode && onRemove ? (
                  <button
                    type="button"
                    className="btn ghost sm"
                    style={{ color: "var(--danger-600)" }}
                    onClick={() => {
                      if (
                        typeof window !== "undefined" &&
                        window.confirm("Remover este widget do painel?")
                      ) {
                        onRemove();
                      }
                    }}
                  >
                    Remover widget
                  </button>
                ) : null}
                {isEditMode && onResetSignalConfig ? (
                  <button
                    type="button"
                    className="btn ghost sm"
                    onClick={() => setConfirmingReset(true)}
                    aria-expanded={confirmingReset}
                    title="Mostra o que será apagado antes de resetar"
                  >
                    Voltar ao padrão do sinal
                  </button>
                ) : null}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" className="btn ghost" onClick={handleGuardedClose}>
                  Cancelar
                </button>
                <button type="submit" className="btn primary">
                  {isEditMode ? "Salvar alterações" : "Adicionar widget"}
                </button>
              </div>
            </div>
          </div>

          <aside className="modal__split-preview" aria-label="Pré-visualização do widget">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 8,
              }}
            >
              <div className="legend">Pré-visualização</div>
              <div
                className="seg"
                role="group"
                aria-label="Modo da pré-visualização"
                style={{ fontSize: 11 }}
              >
                <button
                  type="button"
                  className={previewMode === "live" ? "on" : ""}
                  onClick={() => setPreviewMode("live")}
                  aria-pressed={previewMode === "live"}
                >
                  Ao vivo
                </button>
                <button
                  type="button"
                  className={previewMode === "simulate" ? "on" : ""}
                  onClick={() => setPreviewMode("simulate")}
                  aria-pressed={previewMode === "simulate"}
                >
                  Simular
                </button>
              </div>
            </div>

            {previewMode === "simulate" ? (
              <div className="inline-warning" role="status">
                <strong>Simulação · dados fictícios</strong>
              </div>
            ) : null}

            {previewMode === "live" && previewNeedsMeasuredData ? (
              <div className="w-hero" role="status">
                <strong>{previewWidget?.title}</strong>
                <p>{previewNeedsAccumulatorDelta
                  ? "A atividade pelo contador depende do avanço entre leituras e é calculada no dashboard após salvar o widget."
                  : "O histórico real é consultado no dashboard após salvar o widget."}</p>
                <p className="hint">Use Simular para visualizar o formato com dados fictícios.</p>
              </div>
            ) : previewWidget && previewValue ? (
              <SignalCard
                widget={previewWidget}
                value={previewValue}
                values={previewValues}
                history={previewHistory}
                histories={previewHistories}
                sourceLabels={previewSourceLabels}
                sourceUnit={selectedInfo?.suggestedUnit}
                availability={previewAvailability}
                editable={false}
              />
            ) : (
              <div className="muted" style={{ fontSize: 12 }}>
                Selecione uma fonte para ver o preview.
              </div>
            )}

            {previewMode === "live" && !previewNeedsMeasuredData && liveValueIsStale ? (
              <div
                className="inline-warning"
                role="status"
                style={{ fontSize: 12 }}
              >
                Sem telemetria fresca para esta fonte. Mude para{" "}
                <strong>Simular</strong> acima para ver como o card vai aparecer com
                dados fictícios.
              </div>
            ) : null}

            {previewMode === "simulate" && previewWidget ? (
              <fieldset
                className="modal__fieldset"
                style={{ marginTop: 4 }}
                aria-label="Controles do simulador"
              >
                <legend>Simulador</legend>

                {widgetType === "led" ? (
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      fontSize: 12,
                    }}
                  >
                    <span>Estado:</span>
                    <select
                      className="input field__control"
                      value={String(simBoolState)}
                      onChange={(event) => setSimBoolState(Number(event.target.value) as 0 | 1)}
                      style={{ flex: 1 }}
                    >
                      <option value="1">Ligado (s = 1)</option>
                      <option value="0">Desligado (s = 0)</option>
                    </select>
                  </label>
                ) : null}

                {widgetType === "availability" || widgetType === "production-status" ? (
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      fontSize: 12,
                    }}
                  >
                    <span>Estado:</span>
                    <select
                      className="input field__control"
                      value={String(simBoolState)}
                      onChange={(event) => setSimBoolState(Number(event.target.value) as 0 | 1)}
                      style={{ flex: 1 }}
                    >
                      <option value="1">
                        {widgetType === "availability"
                          ? "Disponível"
                          : availabilityMode === "activity"
                            ? "Em movimento"
                            : "Produzindo"}
                      </option>
                      <option value="0">
                        {widgetType === "availability"
                          ? "Indisponível"
                          : availabilityMode === "activity"
                            ? "Sem movimento"
                            : "Parado"}
                      </option>
                    </select>
                  </label>
                ) : null}

                {widgetType === "number" ||
                widgetType === "gauge" ||
                widgetType === "progress" ||
                widgetType === "production-kpi" ? (
                  <label
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                      fontSize: 12,
                    }}
                  >
                    <span style={{ display: "flex", justifyContent: "space-between" }}>
                      <span>Valor</span>
                      <strong className="mono">
                        {Number.isFinite(simNumeric) ? simNumeric.toFixed(1) : "—"}
                      </strong>
                    </span>
                    <input
                      type="range"
                      min={simRange.min}
                      max={simRange.max}
                      step={simRange.step}
                      value={simNumeric}
                      onChange={(event) => setSimNumeric(Number(event.target.value))}
                      aria-label="Valor simulado"
                    />
                    <span
                      className="muted mono"
                      style={{ display: "flex", justifyContent: "space-between", fontSize: 10 }}
                    >
                      <span>{simRange.min}</span>
                      <span>{simRange.max}</span>
                    </span>
                  </label>
                ) : null}

                <label
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 12,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!simIsAvailable}
                    onChange={(event) => setSimIsAvailable(!event.target.checked)}
                  />
                  <span>Forçar &quot;sem sinal&quot;</span>
                </label>
              </fieldset>
            ) : null}

            {!(previewMode === "live" && previewNeedsMeasuredData) && <p className="hint">
              {previewMode === "simulate"
                ? "Valor simulado — só para visualização. Nada é salvo do simulador."
                : liveValueIsStale
                    ? "Sem telemetria fresca."
                    : previewValues?.some((value) => !value.isAvailable)
                      ? "Leituras reais disponíveis. Fontes sem telemetria fresca aparecem sem valor."
                      : "Valor real vindo da telemetria atual."}
            </p>}
          </aside>
        </form>
      </div>
    </div>
  );
}

function renderSourceOptions(sources: AvailableSource[]) {
  const signals = sources.filter((s) => s.kind === "signal");

  return (
    <>
      {signals.length > 0 ? (
        <optgroup label="Sinais do gateway">
          {signals.map((s) => (
            <option key={s.source} value={s.source}>
              {s.source} — {s.label}
            </option>
          ))}
        </optgroup>
      ) : null}
    </>
  );
}

/**
 * Reconstrói um `AvailableSource` mínimo a partir de uma tag técnica.
 * Usado em modo edit, quando a lista `sources` não está disponível mas
 * precisamos consultar a matriz de compatibilidade do widget existente.
 */
function deriveSourceFromTag(tag: string): AvailableSource | undefined {
  const real = /^G(\d+)_P(\d+)_(s|acc|v)$/i.exec(tag);
  if (real) {
    return {
      source: tag,
      label: tag,
      kind: "signal",
      signal: real[3] as "s" | "acc" | "v",
    };
  }
  const calc = /^V(\d+)$/i.exec(tag);
  if (calc) {
    return { source: tag, label: tag, kind: "calc" };
  }
  return undefined;
}

function parseOptionalNumber(value: string): number | null | "invalid" {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : "invalid";
}

function collectThresholds(
  supports: boolean,
  warnAt: string,
  dangerAt: string,
  warnDirection: "above" | "below" = "above",
  dangerDirection: "above" | "below" = "above",
): WidgetThreshold[] | undefined {
  if (!supports) return undefined;
  const out: WidgetThreshold[] = [];
  const warn = parseOptionalNumber(warnAt);
  const danger = parseOptionalNumber(dangerAt);
  if (warn !== null && warn !== "invalid") {
    out.push({
      at: warn,
      tone: "warn",
      ...(warnDirection === "below" ? { direction: "below" as const } : null),
    });
  }
  if (danger !== null && danger !== "invalid") {
    out.push({
      at: danger,
      tone: "danger",
      ...(dangerDirection === "below" ? { direction: "below" as const } : null),
    });
  }
  return out.length > 0 ? out : undefined;
}

/**
 * H3 — valor totalmente sintético controlado pelos sliders/switches do
 * simulador. Aplica thresholds via `resolveTone` para que o card pinte
 * cores corretamente conforme o operador arrasta o slider.
 *
 * Cada tipo de widget consome um subconjunto das opções:
 * - LED: usa `boolState` como numericValue (0 ou 1) e ignora `numeric`.
 * - Availability/Production-status: usa `boolState` (1=disponível/produzindo).
 * - Production-KPI: usa `numeric` como valor; também monta um
 *   `accumulatorDelta` sintético conforme `accStatus` para que o widget
 *   diferencie warming/reset/ready visualmente.
 * - Number/Gauge/Progress: usa `numeric` direto.
 */
function makeSimulatedValue(
  widget: DashboardWidget,
  options: {
    numeric: number;
    boolState: 0 | 1;
    isAvailable: boolean;
  },
): ResolvedWidgetValue {
  const address = parseAddressSafe(widget.source);
  const base: ResolvedWidgetValue = {
    source: widget.source,
    address,
    value: 0,
    numericValue: 0,
    measuredAt: new Date().toISOString(),
    tone: "neutral",
    isAvailable: options.isAvailable,
  };

  if (!options.isAvailable) {
    return { ...base, numericValue: null, value: null, tone: "neutral" };
  }

  if (widget.widgetType === "led") {
    return {
      ...base,
      value: options.boolState,
      numericValue: options.boolState,
      tone: "ok",
    };
  }

  if (widget.widgetType === "availability" || widget.widgetType === "production-status") {
    return {
      ...base,
      value: options.boolState,
      numericValue: options.boolState,
      tone: options.boolState === 1 ? "ok" : "warn",
    };
  }

  // Number / Gauge / Progress / Production-KPI.
  return {
    ...base,
    value: options.numeric,
    numericValue: options.numeric,
    tone: resolveTone(options.numeric, widget.settings.thresholds),
  };
}

/**
 * Ausência de leitura no modo Ao vivo. Exemplos exigem escolha de Simular;
 * não preencher o meio da escala, estado ligado ou horário de medição fictício.
 */
function makeMissingPreviewValue(source: string): ResolvedWidgetValue {
  return {
    source,
    address: parseAddressSafe(source),
    value: null,
    numericValue: null,
    measuredAt: null,
    tone: "neutral",
    quality: "waiting",
    isAvailable: false,
  };
}

function parseAddressSafe(source: string): ResolvedWidgetValue["address"] {
  // Tentativa best-effort. Quando inválido (deveria ser raro no preview),
  // devolve um endereço dummy para satisfazer o tipo — o SignalCard só
  // usa `kind` e `slotIndex` para o `formatPublicSourceLabel`, então
  // mostrar a tag bruta é OK.
  const tag = /^G(\d+)_P(\d+)_(s|acc|v)$/i.exec(source);
  if (tag) {
    return {
      kind: "real",
      slotIndex: Number(tag[1]),
      portIndex: Number(tag[2]),
      signal: tag[3] as "s" | "acc" | "v",
    };
  }
  const derived = /^V(\d+)$/i.exec(source);
  if (derived) {
    return { kind: "derived", derivedIndex: Number(derived[1]) };
  }
  return { kind: "real", slotIndex: 0, portIndex: 0, signal: "acc" };
}
